import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';
import { apiClient } from '../services/apiService';

function isNoNewDataImportMessage(...messages) {
  const normalizedMessage = messages
    .filter(Boolean)
    .map(message => String(message).toLowerCase())
    .join(' ');

  return normalizedMessage.includes('already exist') ||
    normalizedMessage.includes('already synchronized') ||
    normalizedMessage.includes('no new data') ||
    normalizedMessage.includes('no changes detected');
}

function hasMeaningfulImportResult(result) {
  const noDataOnly = (result?.noNewData === true || result?.dataSync === true) &&
    Number(result?.createdCount || 0) === 0 &&
    Number(result?.updatedCount || 0) === 0 &&
    Number(result?.unchangedCount || 0) === 0 &&
    Number(result?.skippedCount || 0) === 0 &&
    Number(result?.alreadySubmittedCount || 0) === 0 &&
    Number(result?.failedCount || 0) === 0 &&
    Number(result?.successCount || 0) === 0;

  if (noDataOnly) return false;

  return Boolean(
    result &&
    (
      Number(result.successCount || 0) > 0 ||
      Number(result.failedCount || 0) > 0 ||
      Number(result.createdCount || 0) > 0 ||
      Number(result.updatedCount || 0) > 0 ||
      Number(result.unchangedCount || 0) > 0 ||
      Number(result.skippedCount || 0) > 0 ||
      Number(result.alreadySubmittedCount || 0) > 0 ||
      Number(result.processedCount || 0) > 0 ||
      Number(result.totalInvoices || 0) > 0
    )
  );
}

function getImportCountSummary(data = {}, options = {}) {
  const total = Math.max(
    Number(data.totalInvoices ?? data.total_invoices ?? data.totalCount ?? data.total_count ?? 0),
    Number(options.previousTotal || 0)
  );
  const explicitProcessed = Math.max(
    Number(data.processedCount ?? data.processed_count ?? 0),
    Number(data.currentInvoiceIndex ?? data.current_index ?? 0),
    Number(options.previousProcessed || 0)
  );
  const skipped = Math.max(
    Number(data.skippedCount ?? data.skipped_count ?? 0),
    Number(data.alreadySubmittedCount ?? data.already_submitted_count ?? 0)
  );
  const categorizedProcessed =
    Number(data.createdCount ?? data.created_count ?? 0) +
    Number(data.updatedCount ?? data.updated_count ?? 0) +
    Number(data.unchangedCount ?? data.unchanged_count ?? 0) +
    Number(data.protectedCount ?? data.protected_count ?? 0) +
    Number(data.conflictCount ?? data.conflict_count ?? 0) +
    Number(data.sourceChangedCount ?? data.source_changed_count ?? 0) +
    Number(data.validationFailedCount ?? data.validation_failed_count ?? 0) +
    Number(data.failedCount ?? data.failed_count ?? 0) +
    skipped;
  const successFallback = Number(data.successCount ?? data.success_count ?? 0);
  const processedBeforeTerminal = Math.max(explicitProcessed, categorizedProcessed, successFallback);
  const finalTotal = Math.max(total, processedBeforeTerminal);
  const processed = options.terminal && finalTotal > 0
    ? Math.max(processedBeforeTerminal, finalTotal)
    : processedBeforeTerminal;

  return {
    total: finalTotal,
    processed: finalTotal > 0 ? Math.min(finalTotal, processed) : processed,
    categorizedProcessed
  };
}

function setLatestScan(state, message, timestamp = Date.now()) {
  state.latestScanMessage = message || 'No new changes detected since the last scan.';
  state.latestScanAt = timestamp;
  state.latestScanResult = {
    message: state.latestScanMessage,
    timestamp
  };
}

const ACTIVE_IMPORT_STATUSES = ['starting', 'converting', 'uploading', 'retrying', 'processing', 'triggered', 'running', 'finalizing'];
const TERMINAL_IMPORT_STATUSES = ['completed', 'completed_with_errors', 'error', 'failed', 'cancelled'];
const STALE_IMPORT_PROGRESS_MS = 2 * 60 * 1000;
// Heartbeat older than this (seconds) marks the service Disconnected in the connection badge,
// even if a stale doc still reports isConnected. Backend sends heartbeats every ~5s.
export const LIVE_HEARTBEAT_THRESHOLD_SECONDS = 30;
const DISCONNECTED_SERVICE_STATUSES = ['disconnected', 'stopped', 'error'];

function hasActiveImportSession(state) {
  return Boolean(state.activeImportSession && !TERMINAL_IMPORT_STATUSES.includes(state.activeImportSession.status)) ||
    state.isImporting === true ||
    ACTIVE_IMPORT_STATUSES.includes(state.status);
}

function hasLockedImportProgress(state) {
  return hasActiveImportSession(state) ||
    (
      Number(state.progress || 0) > 0 &&
      Number(state.progress || 0) < 100 &&
      (
        Number(state.totalInvoices || 0) > 0 ||
        Number(state.currentInvoiceIndex || 0) > 0 ||
        Number(state.processedCount || 0) > 0
      )
    );
}

function preserveActiveImportFromNonProgressUpdate(state, incomingStatus) {
  return hasLockedImportProgress(state) && incomingStatus !== 'cancelled';
}

function getIncomingProgressNumbers(data = {}) {
  const total = Number(data.totalInvoices ?? data.total_invoices ?? 0);
  const current = Number(data.currentInvoiceIndex ?? data.current_index ?? 0);
  const processed = Number(data.processedCount ?? data.processed_count ?? 0);
  return { total, current, processed };
}

function getActiveImportSnapshot(session) {
  if (!session) return null;
  return {
    sessionId: session.sessionId,
    status: session.status,
    progress: session.progress,
    processed: session.processed,
    total: session.total,
    startedAt: session.startedAt,
    completedAt: session.completedAt || null,
    updatedAt: session.updatedAt || null
  };
}

function logActiveImportWrite(source, previous, next, reason) {
  if (typeof console === 'undefined') return;
  console.info('[ACTIVE_IMPORT_WRITE]', {
    source,
    importSessionId: next?.sessionId ?? previous?.sessionId ?? reason?.incomingSessionId ?? null,
    oldProgress: previous?.progress ?? null,
    newProgress: next?.progress ?? null,
    oldProcessed: previous?.processed ?? null,
    newProcessed: next?.processed ?? null,
    previous,
    newState: next,
    reason,
    timestamp: new Date().toISOString()
  });
}

function getPayloadProgress(payload = {}) {
  const total = Number(payload.totalInvoices ?? payload.total_invoices ?? 0);
  const processed = Math.max(
    Number(payload.processedCount ?? payload.processed_count ?? 0),
    Number(payload.currentInvoiceIndex ?? payload.current_index ?? 0)
  );
  if (total > 0 && processed > 0) {
    return Math.max(0, Math.min(100, Math.round((Math.min(processed, total) / total) * 100)));
  }

  const value = payload.percentage ?? payload.progress;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(0, Math.min(100, numeric)) : 0;
}

function getPayloadSessionId(payload = {}) {
  return payload.importSessionId || payload.sessionId || payload.jobId || payload.importJobId || null;
}

function statusToImportStage(status) {
  if (status === 'starting' || status === 'triggered') return 'starting';
  if (status === 'converting') return 'converting';
  if (status === 'finalizing') return 'finalizing';
  if (status === 'completed' || status === 'completed_with_errors') return 'completed';
  if (status === 'error') return 'failed';
  if (status === 'failed' || status === 'cancelled') return status;
  if (['uploading', 'processing', 'retrying', 'running'].includes(status)) return 'uploading';
  return status || 'uploading';
}

function upsertActiveImportSessionFromProgress(state, payload = {}, source = 'websocket_progress') {
  const previous = getActiveImportSnapshot(state.activeImportSession);
  const incomingStatus = statusToImportStage(payload.status || payload.serviceStatus);
  const incomingProgress = getPayloadProgress(payload);
  const { total: incomingTotal, current: incomingCurrent, processed: incomingProcessed } = getIncomingProgressNumbers(payload);
  const existing = state.activeImportSession;
  const incomingSessionId = getPayloadSessionId(payload);
  const existingSessionId = existing?.sessionId || state._importSessionId || null;
  const existingTotal = Number(existing?.total || 0);
  const existingProcessed = Number(existing?.processed || state.processedCount || state.currentInvoiceIndex || 0);
  const incomingBestProcessed = Math.max(incomingProcessed || 0, incomingCurrent || 0);
  const incomingSequence = Number.isFinite(Number(payload.sequence)) ? Number(payload.sequence) : null;
  const existingSequence = Number(existing?.sequence || 0);
  const isTerminal = TERMINAL_IMPORT_STATUSES.includes(incomingStatus);
  const hasExistingActiveSession = Boolean(existing && !TERMINAL_IMPORT_STATUSES.includes(existing.status));
  const isMissingSessionBatchUpdate = hasExistingActiveSession &&
    existingSessionId &&
    !incomingSessionId &&
    incomingTotal > 0 &&
    existingTotal > incomingTotal;
  const isMismatchedSessionUpdate = hasExistingActiveSession &&
    existingSessionId &&
    incomingSessionId &&
    incomingSessionId !== existingSessionId;
  const isBackwardCounterUpdate = hasExistingActiveSession &&
    incomingTotal > 0 &&
    incomingBestProcessed > 0 &&
    incomingBestProcessed < existingProcessed &&
    incomingProgress < Number(existing?.progress || 0);
  // Same-session update that arrived out of order (older sequence than what we already applied).
  const isStaleSequenceUpdate = hasExistingActiveSession &&
    incomingSequence != null &&
    existingSequence > 0 &&
    incomingSessionId &&
    incomingSessionId === existingSessionId &&
    incomingSequence < existingSequence;

  // A finished (terminal) session must not be resurrected by a lagging non-terminal poll/heartbeat
  // for the SAME import. Only a genuinely new import (different importSessionId) may open a new active
  // session; otherwise one stale "uploading 170" poll arriving after completion re-pins the card.
  const existingIsTerminal = Boolean(existing && TERMINAL_IMPORT_STATUSES.includes(existing.status));
  const isDifferentSession = Boolean(incomingSessionId && existingSessionId && incomingSessionId !== existingSessionId);
  const isLaggingResurrection = existingIsTerminal && !isTerminal && !isDifferentSession;

  if (isMissingSessionBatchUpdate || isMismatchedSessionUpdate || isBackwardCounterUpdate || isStaleSequenceUpdate || isLaggingResurrection) {
    logActiveImportWrite(
      source,
      previous,
      previous,
      {
        accepted: false,
        incomingSessionId,
        existingSessionId,
        incomingProgress,
        incomingProcessed,
        incomingCurrent,
        incomingTotal,
        incomingSequence,
        existingSequence,
        reason: isLaggingResurrection
          ? 'rejected_lagging_resurrection_of_terminal_session'
          : isMismatchedSessionUpdate
            ? 'rejected_session_mismatch'
            : isMissingSessionBatchUpdate
              ? 'rejected_batch_progress_without_session_id'
              : isStaleSequenceUpdate
                ? 'rejected_stale_sequence'
                : 'rejected_backward_counter'
      }
    );
    return false;
  }

  const total = Math.max(existingTotal, incomingTotal || 0);
  // Processed count must come ONLY from confirmed records (processedCount/currentInvoiceIndex),
  // never derived from the percentage. Deriving processed from an estimated percentage produced
  // the "37/180" inflation (floor(21% * 180) = 37) while only ~10 were confirmed. The percentage
  // still drives the visible bar; the "X of Y" count tracks confirmed work only.
  const oldProcessed = Number(existing?.processed || 0);
  const oldProgress = Number(existing?.progress || 0);
  // Only start fresh for a brand-new import (different session id) or a terminal incoming update.
  // (Previously oldProgress>=100 also reset, which let a lagging poll reopen a finished session.)
  const canResetForNewSession = !existing || isTerminal || isDifferentSession;
  const progress = canResetForNewSession
    ? incomingProgress
    : Math.max(oldProgress, incomingProgress);
  const processed = canResetForNewSession
    ? Math.max(incomingProcessed || 0, incomingCurrent || 0)
    : Math.max(oldProcessed, incomingProcessed || 0, incomingCurrent || 0);
  const sessionId = existing?.sessionId ||
    incomingSessionId ||
    `${payload.timestamp || Date.now()}-${total || 'unknown'}`;
  const startedAt = existing?.startedAt || payload.startedAt || payload.startTime || payload.timestamp || new Date().toISOString();
  const completedAt = isTerminal
    ? (payload.completedAt || payload.timestamp || new Date().toISOString())
    : existing?.completedAt || null;

  state.activeImportSession = {
    sessionId,
    status: incomingStatus,
    progress,
    processed: total > 0 ? Math.min(total, processed) : processed,
    total,
    sequence: incomingSequence != null ? Math.max(incomingSequence, existingSequence) : existingSequence,
    currentInvoiceNumber: payload.currentInvoiceNumber ?? payload.current_invoice_number ?? existing?.currentInvoiceNumber ?? null,
    message: payload.message || existing?.message || '',
    startedAt,
    completedAt,
    source,
    updatedAt: payload.timestamp || new Date().toISOString()
  };
  state._importSessionId = sessionId;

  logActiveImportWrite(
    source,
    previous,
    getActiveImportSnapshot(state.activeImportSession),
    {
      incomingProgress,
      incomingSessionId,
      accepted: true,
      incomingProcessed,
      incomingCurrent,
      incomingTotal,
      keptOverallTotal: existingTotal > incomingTotal && incomingTotal > 0,
      ignoredBackwardProgress: !canResetForNewSession && incomingProgress < oldProgress,
      ignoredBackwardProcessed: !canResetForNewSession && Math.max(incomingProcessed || 0, incomingCurrent || 0) < oldProcessed
    }
  );
  return true;
}

function completeActiveImportSession(state, payload = {}, status = 'completed', source = 'websocket_completion') {
  const previous = getActiveImportSnapshot(state.activeImportSession);
  const stats = payload.stats || payload || {};
  const existing = state.activeImportSession;
  const incomingSessionId = getPayloadSessionId(payload) || getPayloadSessionId(stats);
  const existingSessionId = existing?.sessionId || state._importSessionId || null;
  const existingActive = Boolean(existing && !TERMINAL_IMPORT_STATUSES.includes(existing.status));
  const incomingTotalForGuard = Number(stats.totalInvoices ?? stats.total_invoices ?? payload.totalInvoices ?? 0);

  if (existingActive && existingSessionId && (!incomingSessionId || incomingSessionId !== existingSessionId) && incomingTotalForGuard < Number(existing.total || 0)) {
    logActiveImportWrite(
      source,
      previous,
      previous,
      {
        accepted: false,
        reason: incomingSessionId ? 'rejected_completion_session_mismatch' : 'rejected_completion_without_session_id',
        incomingSessionId,
        existingSessionId,
        incomingTotal: incomingTotalForGuard
      }
    );
    return false;
  }

  const countSummary = getImportCountSummary({ ...payload, ...stats }, {
    terminal: true,
    previousTotal: Number(existing?.total || 0),
    previousProcessed: Number(existing?.processed || 0)
  });
  const total = countSummary.total;
  const incomingProcessed = countSummary.processed;
  const processed = total > 0
    ? Math.min(total, Math.max(Number(existing?.processed || 0), incomingProcessed))
    : Math.max(Number(existing?.processed || 0), incomingProcessed);

  state.activeImportSession = {
    ...(existing || {}),
    sessionId: existing?.sessionId || incomingSessionId || `${Date.now()}-${total || 'complete'}`,
    status: statusToImportStage(status),
    progress: status === 'completed' || status === 'completed_with_errors' ? 100 : Number(existing?.progress || 0),
    processed,
    total,
    message: stats.message || payload.message || existing?.message || 'Import completed',
    startedAt: existing?.startedAt || payload.startedAt || new Date().toISOString(),
    completedAt: payload.completedAt || payload.timestamp || new Date().toISOString(),
    source,
    updatedAt: new Date().toISOString()
  };
  state._importSessionId = state.activeImportSession.sessionId;

  logActiveImportWrite(
    source,
    previous,
    getActiveImportSnapshot(state.activeImportSession),
    { terminalStatus: status, accepted: true, incomingSessionId }
  );
  return true;
}

function clearActiveImportSession(state, source, reason) {
  const previous = getActiveImportSnapshot(state.activeImportSession);
  state.activeImportSession = null;
  logActiveImportWrite(source, previous, null, reason);
}

function getProgressSignature(data = {}) {
  const total = data.totalInvoices ?? data.total_invoices ?? 0;
  const current = data.currentInvoiceIndex ?? data.current_index ?? 0;
  const processed = data.processedCount ?? data.processed_count ?? 0;
  const success = data.successCount ?? data.success_count ?? 0;
  const failed = data.failedCount ?? data.failed_count ?? 0;
  const percentage = data.percentage ?? data.progress ?? 0;
  const status = data.status ?? data.serviceStatus ?? '';

  return [status, total, current, processed, success, failed, percentage].join('|');
}

function getImportDebugSnapshot(state) {
  return {
    status: state.status,
    isImporting: state.isImporting,
    progress: state.progress,
    totalInvoices: state.totalInvoices,
    currentInvoiceIndex: state.currentInvoiceIndex,
    processedCount: state.processedCount,
    successCount: state.successCount,
    failedCount: state.failedCount,
    showLastImportResult: state.showLastImportResult,
      latestScanMessage: state.latestScanMessage,
    activeImportSession: getActiveImportSnapshot(state.activeImportSession),
    lastImportResult: state.lastImportResult ? {
      totalInvoices: state.lastImportResult.totalInvoices,
      processedCount: state.lastImportResult.processedCount,
      successCount: state.lastImportResult.successCount,
      failedCount: state.lastImportResult.failedCount,
      createdCount: state.lastImportResult.createdCount,
      updatedCount: state.lastImportResult.updatedCount,
      timestamp: state.lastImportResult.timestamp
    } : null
  };
}

function logImportStateWrite(source, action, previous, next, incoming) {
  if (typeof console === 'undefined') return;
  console.info('[IMPORT_STATE_WRITE]', {
    source,
    action,
    previous,
    newState: next,
    incoming,
    timestamp: new Date().toISOString()
  });
}

function applyLastImportResult(state, result) {
  if (!result) return;

  if (hasLockedImportProgress(state)) {
    state.lastImportResult = result;
    state.showLastImportResult = false;
    return;
  }

  const incomingNoNewData = result.noNewData === true ||
    isNoNewDataImportMessage(result.message, state.message);
  const incomingMeaningfulResult = hasMeaningfulImportResult(result);

  if (incomingNoNewData) {
    setLatestScan(state, 'No new changes detected since the last scan.', result.timestamp || Date.now());
    state.showLastImportResult = hasMeaningfulImportResult(state.lastImportResult);
  } else if (!incomingMeaningfulResult) {
    setLatestScan(state, 'No import activity detected.', result.timestamp || Date.now());
    state.showLastImportResult = hasMeaningfulImportResult(state.lastImportResult);
  } else {
    state.lastImportResult = result;
    state.showLastImportResult = true;
    state.latestScanMessage = null;
    state.latestScanAt = null;
  }
}

// Async thunks for auto-import operations
export const getAutoImportStatus = createAsyncThunk(
  'autoImport/getStatus',
  async (_, { rejectWithValue }) => {
    try {
      // Use the correct service endpoint that returns proper connection status
      const response = await apiService.invoices.getAutoImportStatus();
      
      // Validate response structure
      // The API service now consistently returns the response payload: { status: 'success', data: serviceStatusData }
      // We must extract the inner serviceStatusData object (which contains isRunning, isConnected, etc.)
      if (!response || response.status !== 'success' || !response.data) {
        // If the new standard wrapper is missing, throw an error
        throw new Error('Invalid response structure: status not success or missing data field');
      }
      
      const serviceStatusData = response.data; // This is the object containing isRunning, isConnected, etc.
      
      // CRITICAL FIX: Force boolean conversion to ensure correct state updates
      // This prevents issues where the API returns true but frontend shows false
      const normalizedData = {
        ...serviceStatusData,
        isConnected: serviceStatusData.isConnected === true,
        isRunning: serviceStatusData.isRunning === true,
        isHealthy: serviceStatusData.isHealthy === true,
        // Ensure serviceStatus is a string
        serviceStatus: serviceStatusData.serviceStatus || serviceStatusData.status || 'unknown',
        // CRITICAL FIX: Include timestamp to track API call freshness
        _lastApiCallTime: Date.now()
      };
      
      // Verbose logging removed - status data processed silently
      
      // The fulfilled reducer expects the service status data object (not the wrapper)
      return normalizedData;
    } catch (error) {
      console.error('[autoImportSlice] getAutoImportStatus error:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to get auto-import status');
    }
  }
);

// Helper function to check if two messages are similar (same phase/intent)
// This prevents rapid message updates that appear as duplicates
// CRITICAL FIX: Relaxed to allow progress-only updates through
function checkMessageSimilarity(currentMsg, newMsg) {
  if (!currentMsg || !newMsg) return false;
  
  // Normalize messages for comparison
  const normalize = (msg) => msg.toLowerCase().trim();
  const current = normalize(currentMsg);
  const newMsg_norm = normalize(newMsg);
  
  // If messages are identical, they're similar
  if (current === newMsg_norm) return true;
  
  // CRITICAL FIX: Check for high word overlap ( > 70% of words are the same)
  // Increased from 50% to 70% to be more strict about what counts as "similar"
  const currentWords = current.split(/\s+/);
  const newWords = newMsg_norm.split(/\s+/);
  const commonWords = currentWords.filter(w => newWords.includes(w));
  const overlapRatio = commonWords.length / Math.max(currentWords.length, newWords.length);
  
  // CRITICAL FIX: Only consider similar if overlap is very high (>70%)
  // This allows progress updates with slightly different messages to pass through
  return overlapRatio > 0.7;
}

// WebSocket event handlers for auto-import status updates
export const handleAutoImportStatusUpdate = (state, action) => {
  const statusData = action.payload;
  
  // CRITICAL FIX: Check if we have a recent API response that should take precedence
  // If the API was called within the last 5 seconds, be more cautious about overriding
  const now = Date.now();
  const lastApiCall = state._lastApiCallTime || 0;
  const apiResponseFresh = (now - lastApiCall) < 5000; // 5 seconds - reduced for better responsiveness
  
  // CRITICAL FIX: Handle both new and legacy formats from WebSocket
  // Extract data from either format (WebSocket may wrap in payload or send directly)
  const data = statusData.payload || statusData;
  
  // Handle new standardized format from WebSocket
  if (data.isRunning !== undefined && data.isConnected !== undefined) {
    console.info('[AUTO_IMPORT_PROGRESS_READ]', {
      source: 'FRONTEND_WEBSOCKET_STATUS',
      timestamp: new Date().toISOString(),
      previous: {
        status: state.status,
        progress: state.progress,
        processed: Math.max(state.currentInvoiceIndex || 0, state.processedCount || 0),
        total: state.totalInvoices || 0
      },
      incoming: {
        status: data.serviceStatus || data.status,
        progress: data.progress,
        processed: Math.max(data.currentInvoiceIndex || 0, data.processedCount || 0),
        total: data.totalInvoices || 0,
        importSessionId: data.importSessionId || data.jobId || null
      }
    });
    // CRITICAL FIX: Allow WebSocket to update connection status during active operations
    // WebSocket is more reliable for real-time status during imports
    const isActiveOperation = ACTIVE_IMPORT_STATUSES.includes(data.status || data.serviceStatus);
    
    if (isActiveOperation) {
      // During active operations, trust WebSocket for real-time updates
      state.isConnected = data.isConnected === true;
      state.isRunning = data.isRunning === true;
      
      // Map serviceStatus to our status field
      const serviceStatus = data.serviceStatus || data.status;
      if (serviceStatus) {
        state.status = serviceStatus;
      }
      
      // Update progress during active operations
      if (data.progress !== undefined) {
        state.progress = data.progress || 0;
      }
      
      // Update message
      if (data.message) {
        state.message = data.message;
      }
    } else if (!apiResponseFresh) {
      // When API is stale, allow WebSocket updates for idle/disconnected states
      if (data.isConnected === false) {
        state.isConnected = false;
        state.isRunning = false;
      }
      
      const serviceStatus = data.serviceStatus || data.status;
      if (serviceStatus) {
        state.status = serviceStatus;
      }
      
      if (data.message && 
          !data.message.includes('connected') && 
          !data.message.includes('disconnected')) {
        state.message = data.message;
      }
    } else {
      // API is fresh, only update non-connection fields
      if (data.message && 
          !data.message.includes('connected') && 
          !data.message.includes('disconnected')) {
        state.message = data.message;
      }
    }
    
    if (data.lastActivity) {
      state.lastCheck = data.lastActivity;
    }
    
    state.serviceVersion = data.service_version || null;
    
  } else if (data.status !== undefined || data.message !== undefined) {
    // Legacy format from WebSocket
    
    // CRITICAL FIX: During active operations, allow status updates from WebSocket
    const isActiveOperation = ACTIVE_IMPORT_STATUSES.includes(data.status);
    
    if (isActiveOperation) {
      // Trust WebSocket during active operations
      if (data.status !== undefined) {
        state.status = data.status;
      }
      if (data.message !== undefined) {
        state.message = data.message;
      }
      if (data.progress !== undefined) {
        state.progress = data.progress;
      }
      // Assume connected during active operations
      state.isConnected = true;
      state.isRunning = true;
    } else if (!apiResponseFresh) {
      // API is stale, allow updates
      if (data.status !== undefined) {
        state.status = data.status;
      }
      if (data.message !== undefined) {
        state.message = data.message;
      }
      if (data.progress !== undefined) {
        state.progress = data.progress;
      }
    } else {
      // API is fresh, only update message if not connection-related
      if (data.message !== undefined &&
          !data.message.includes('connected') && 
          !data.message.includes('disconnected')) {
        state.message = data.message;
      }
    }
    
    if (data.last_check !== undefined) {
      state.lastCheck = data.last_check;
    }
  }
  
  state.isRefreshing = false;
  state.error = null;
};

// NEW: Handle detailed import progress from WebSocket or HTTP (for auto-imports)
export const handleDetailedImportProgress = (state, action) => {
  const progressData = action.payload;
  
  // CRITICAL FIX: Handle both WebSocket and HTTP progress update formats
  // WebSocket format: payload object with nested data
  // HTTP format: direct data object from API response
  
  // Extract data from either format
  const data = progressData.payload || progressData;
  console.info('[AUTO_IMPORT_PROGRESS_READ]', {
    source: 'FRONTEND_STANDALONE_PROGRESS',
    timestamp: new Date().toISOString(),
    previous: {
      status: state.status,
      progress: state.progress,
      processed: Math.max(state.currentInvoiceIndex || 0, state.processedCount || 0),
      total: state.totalInvoices || 0
    },
    incoming: {
      status: data.status,
      progress: data.percentage ?? data.progress,
      processed: Math.max(data.currentInvoiceIndex || data.current_index || 0, data.processedCount || data.processed_count || 0),
      total: data.totalInvoices ?? data.total_invoices ?? 0,
      importSessionId: data.importSessionId || data.jobId || null
    }
  });
  const progressSignature = getProgressSignature(data);
  if (progressSignature !== state._lastProgressSignature) {
    state._lastProgressSignature = progressSignature;
    state._lastProgressAt = Date.now();
  }
  
  // Track import start time for ETA calculation
  if (state.importStartTime === null && (data.currentInvoiceIndex > 0 || data.current_index > 0)) {
    state.importStartTime = Date.now();
  }
  
  // CRITICAL FIX: Update detailed progress for auto-imports with fallback field names
  // Support both camelCase (WebSocket) and snake_case (HTTP) field names
  
  // CRITICAL FIX: ALWAYS update progress immediately when provided
  // This ensures the progress bar stays in sync with the status line
  if (data.percentage !== undefined || data.progress !== undefined) {
    // Use percentage if available, otherwise use progress
    const newProgress = data.percentage !== undefined ? data.percentage : (data.progress || 0);
    // CRITICAL FIX: Always accept backend progress - it's the source of truth
    state.progress = newProgress;
  }
  
  // Update other fields
  if (data.totalInvoices !== undefined || data.total_invoices !== undefined) {
    state.totalInvoices = data.totalInvoices || data.total_invoices || 0;
  }
  if (data.currentInvoiceIndex !== undefined || data.current_index !== undefined) {
    state.currentInvoiceIndex = data.currentInvoiceIndex || data.current_index || 0;
  }
  if (data.currentInvoiceNumber !== undefined || data.current_invoice_number !== undefined) {
    state.currentInvoiceNumber = data.currentInvoiceNumber || data.current_invoice_number || null;
  }
  if (data.processedCount !== undefined || data.processed_count !== undefined) {
    state.processedCount = data.processedCount || data.processed_count || 0;
  }
  if (data.successCount !== undefined || data.success_count !== undefined) {
    state.successCount = data.successCount || data.success_count || 0;
  }
  if (data.failedCount !== undefined || data.failed_count !== undefined) {
    state.failedCount = data.failedCount || data.failed_count || 0;
  }
  
  // CRITICAL FIX: Message deduplication - only update message if it's meaningfully different
  // This prevents rapid message changes that appear as "duplicate" messages to users
  if (data.message !== undefined && data.message !== null) {
    const currentMessage = state.message || '';
    const newMessage = data.message;
    
    // Only update if:
    // 1. Current message is empty, OR
    // 2. New message is completely different (not just a variation), OR
    // 3. Progress has changed significantly (> 5%), OR
    // 4. Status has changed
    const progressChanged = data.percentage !== undefined && 
      Math.abs((data.percentage || 0) - state.progress) > 5;
    const statusChanged = data.status !== undefined && data.status !== state.status;
    
    // Check if messages are similar (same intent/phase)
    const messageSimilarity = checkMessageSimilarity(currentMessage, newMessage);
    
    if (currentMessage === '' || !messageSimilarity || progressChanged || statusChanged) {
      state.message = newMessage;
    }
    // CRITICAL FIX: Removed the else block that was logging skipped messages
    // This reduces console spam and allows similar messages when progress changes
  }
  
  // CRITICAL FIX: Always update status if provided
  if (data.status !== undefined) {
    state.status = data.status;
  }
  
  // Calculate ETA based on processing speed
  if (state.importStartTime && state.currentInvoiceIndex > 0 && state.totalInvoices > 0) {
    const elapsedMs = Date.now() - state.importStartTime;
    const invoicesPerMs = state.currentInvoiceIndex / elapsedMs;
    const remainingInvoices = state.totalInvoices - state.currentInvoiceIndex;
    const remainingMs = remainingInvoices / invoicesPerMs;
    
    state.invoicesPerSecond = Math.round(invoicesPerMs * 1000 * 10) / 10; // Round to 1 decimal
    state.estimatedTimeRemaining = Math.max(0, Math.round(remainingMs / 1000)); // In seconds
  }
  
  // Set importing flag when we have detailed progress
  const isActiveStatus = ACTIVE_IMPORT_STATUSES.includes(data.status);
  if (isActiveStatus) {
    state.isImporting = true;
    state.importType = 'auto';
    state.isConnected = true; // CRITICAL FIX: Ensure connected during active import
  } else if (data.status === 'completed' || data.status === 'completed_with_errors' || data.status === 'error') {
    state.isImporting = false;
    state.estimatedTimeRemaining = 0;
  }
  
  state.isRefreshing = false;
  state.error = null;
};

export const handleImportProgressUpdate = (state, action) => {
  const progressData = action.payload;
  
  // Verbose logging removed - progress updates processed silently
  
  // Update progress and status

  if (progressData.progress !== undefined) {
    state.progress = progressData.progress;
  }
  if (progressData.status !== undefined) {
    state.status = progressData.status;
  }
  if (progressData.message !== undefined) {
    state.message = progressData.message;
  }
  
  // Set connection status to true during active import
  state.isConnected = true;
  state.isRefreshing = false;
  state.error = null;
};

export const handleImportCompleteUpdate = (state, action) => {
  const completeData = action.payload;
  
  // Verbose logging removed - completion updates processed silently
  
  // Update completion data

  if (completeData.processed_invoices !== undefined) {
    state.processedInvoices = completeData.processed_invoices;
  }
  if (completeData.status !== undefined) {
    state.status = completeData.status;
  }
  if (completeData.message !== undefined) {
    state.message = completeData.message;
  }
  
  // Set progress to 100% on completion
  state.progress = 100;
  state.isConnected = true;
  state.isRefreshing = false;
  state.error = null;
};

// Thunk to check if status has meaningfully changed
export const checkStatusChange = createAsyncThunk(
  'autoImport/checkStatusChange',
  async (_, { getState, rejectWithValue }) => {
    try {
      const currentState = getState().autoImport;
      // Use the auto-import status endpoint for accurate connection status
      const response = await apiService.invoices.getAutoImportStatus();
      const newData = response.data; // Now directly contains the status data (isRunning, isConnected, etc.)
      
      // Check if this is a meaningful change
      const isMeaningfulChange = hasMeaningfulStatusChange(currentState, newData);
      
      return {
        data: newData,
        isMeaningfulChange
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to check status change');
    }
  }
);

// Helper function to determine if status change is meaningful
function hasMeaningfulStatusChange(currentState, newData) {
  // Always consider it meaningful if:
  // 1. Service connection status changed (CRITICAL: More sensitive to disconnection)
  // 2. Status changed from idle to active or vice versa
  // 3. Progress changed significantly (more than 10%)
  // 4. New invoices were processed
  // 5. Error occurred
  // 6. Authentication errors that indicate persistent issues
  
  const currentStatus = currentState.status || 'unknown';
  const newStatus = newData.status || 'unknown';
  const currentConnected = currentState.isConnected;
  const newConnected = newData.isConnected !== undefined ? newData.isConnected : 
                      (newData.status !== 'disconnected' && !newData.message?.includes('not connected'));
  
  // Connection status change is ALWAYS meaningful (especially disconnection)
  if (currentConnected !== newConnected) {
    // Connection status change detected - no need to log in production
    return true;
  }

  
  // Status change from idle to active (or vice versa) is meaningful
  // Also consider 'completed' as a meaningful state change
  const wasIdle = currentStatus === 'idle';
  const nowActive = ACTIVE_IMPORT_STATUSES.includes(newStatus);
  const wasActive = ACTIVE_IMPORT_STATUSES.includes(currentStatus);
  const nowIdle = newStatus === 'idle';
  const nowCompleted = newStatus === 'completed' || newStatus === 'completed_with_errors';
  const wasCompleted = currentStatus === 'completed' || currentStatus === 'completed_with_errors';
  
  if ((wasIdle && nowActive) || (wasActive && nowIdle) || (wasActive && nowCompleted) || (wasCompleted && nowIdle)) {
    return true;
  }

  
  // Progress change of more than 10% is meaningful
  const currentProgress = currentState.progress || 0;
  const newProgress = newData.progress || 0;
  if (Math.abs(newProgress - currentProgress) > 10) {
    return true;
  }
  
  // New invoices processed is meaningful
  const currentProcessed = currentState.processedInvoices || [];
  const newProcessed = newData.processed_invoices || [];
  if (newProcessed.length > currentProcessed.length) {
    return true;
  }
  
  // Error state is meaningful
  if (newStatus === 'error') {
    return true;
  }
  
  // Authentication errors that indicate persistent issues are meaningful
  const newMessage = newData.message || '';
  const isPersistentAuthError = newMessage.includes('User ID not found') ||
                                newMessage.includes('authentication') ||
                                newMessage.includes('unauthorized');
  
  if (isPersistentAuthError) {
    return true;
  }
  
  // "No changes detected" messages should be filtered out unless they're the first occurrence
  // or there's been a significant time gap (5+ minutes)
  if (newData.message?.includes('No changes detected')) {
    const lastCheck = currentState.lastCheck;
    const newCheck = newData.last_check;
    
    if (!lastCheck) return true; // First check
    
    const timeDiff = new Date(newCheck) - new Date(lastCheck);
    const timeDiffMinutes = timeDiff / (1000 * 60);
    
    // Only consider meaningful if it's been more than 5 minutes
    return timeDiffMinutes > 5;
  }
  
  return false;
}

export const triggerAutoImport = createAsyncThunk(
  'autoImport/trigger',
  async (_, { rejectWithValue }) => {
    try {
      // First, check for missing invoices
      const checkResponse = await apiClient.post('/invoices/check-missing');
      const missingInvoices = checkResponse.data.data || [];
      
      if (missingInvoices.length > 0) {
        // Use smart recovery to handle missing invoices
        const recoveryResponse = await apiClient.post('/invoices/smart-recovery', {
          forceRecovery: false,
          checkOnly: false
        });
        return recoveryResponse.data;
      } else {
        // Fall back to regular manual import trigger
        const response = await apiService.invoices.triggerAutoImport();
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// NEW: Execute manual import with progress tracking
export const executeManualImport = createAsyncThunk(
  'autoImport/executeManualImport',
  async (options = {}, { rejectWithValue }) => {
    try {
      const { forceImport = false, checkMissing = false } = options;
      
      const response = await apiClient.post('/invoices/manual-import', {
        forceImport,
        checkMissing
      });
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// NEW: Get import progress for polling
export const getImportProgress = createAsyncThunk(
  'autoImport/getImportProgress',
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/invoices/import-progress/${jobId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAutoImportStatus = createAsyncThunk(
  'autoImport/updateStatus',
  async (statusData, { rejectWithValue }) => {
    try {
      const response = await apiService.invoices.updateAutoImportStatus(statusData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update auto-import status');
    }
  }
);

// Initial state
const initialState = {
  status: 'unknown',
  isRunning: false, // Added isRunning field to match backend response
  progress: 0,
  message: '',
  lastProcessedHash: null,
  lastFileModTime: null,
  processedInvoices: [],
  lastCheck: null,
  serviceVersion: null,
  config: null, // Added config state
  isConnected: false, // false = not connected (default), true = connected
  isRunning: false,
  isHealthy: false,
  secondsSinceLastHeartbeat: null,
  lastHeartbeat: null,
  isLoading: true, // NEW: Track initial loading state
  isRefreshing: false,
  error: null,
  lastStatsRefresh: null,
  activeImportSession: null,
  serviceStatus: {
    status: 'unknown',
    isRunning: false,
    isConnected: false,
    isHealthy: false,
    message: ''
  },
  latestScanResult: null,
  showImportNotification: false,
  // NEW: Manual import progress tracking
  importJobId: null,
  importType: null, // 'manual' or 'auto'
  totalInvoices: 0,
  currentInvoiceIndex: 0,
  currentInvoiceNumber: null,
  processedCount: 0,
  successCount: 0,
  failedCount: 0,
  failedInvoices: [],
  importStartTime: null,
  importEndTime: null,
  importDuration: null,
  importResults: null,
  isImporting: false,
  showImportResultModal: false,
  // NEW: ETA calculation fields
  estimatedTimeRemaining: 0,
  invoicesPerSecond: 0,
  // CRITICAL FIX: Track if import was manually triggered by user
  // This ensures progress/modal only shows for user-initiated actions
  isManuallyTriggered: false,
  // NEW: Track the source of the import trigger
  triggerSource: null, // 'manual', 'service-control', 'scheduled', 'auto-connect', 'background'
  // CRITICAL FIX: Track last API call time to prioritize API over WebSocket
  _lastApiCallTime: 0,
  // CRITICAL FIX: Track if import result modal has been shown for current import
  // This prevents modal from appearing multiple times
  hasShownImportResultForCurrentImport: false,
  // CRITICAL FIX: Import session ID to track current import session
  // This prevents cross-session contamination and duplicate modal displays
  _importSessionId: null,
  // CRITICAL FIX: Track last progress update timestamp to prevent duplicate updates
  _lastProgressUpdateTime: 0,
  _lastProgressAt: 0,
  _lastProgressSignature: null,
  // CRITICAL FIX: Track last completion timestamp to prevent duplicate completions
  _lastCompletionTime: 0,
  // CRITICAL FIX: Track last completion key to prevent duplicate modal displays
  _lastCompletionKey: null,
  // CRITICAL FIX: Minimum time between modal displays (prevents rapid re-showing)
  _minModalDisplayInterval: 5000, // 5 seconds
  // NEW: Persist last import result to show even after status changes to idle
  lastImportResult: null,
  // NEW: Control whether to show last import result
  showLastImportResult: false,
  latestScanMessage: null,
  latestScanAt: null
};

// Auto-import slice
const autoImportSlice = createSlice({
  name: 'autoImport',
  initialState,
  extraReducers: (builder) => {
    builder
      // Get status
      .addCase(getAutoImportStatus.pending, (state) => {
        state.isRefreshing = true;
        if (!state.lastCheck && state.status === 'unknown') {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(getAutoImportStatus.fulfilled, (state, action) => {
        const statusData = action.payload;
        const previousState = getImportDebugSnapshot(state);
        console.info('[AUTO_IMPORT_PROGRESS_READ]', {
          source: 'FRONTEND_STATUS_ENDPOINT',
          timestamp: new Date().toISOString(),
          previous: {
            status: state.status,
            progress: state.progress,
            processed: Math.max(state.currentInvoiceIndex || 0, state.processedCount || 0),
            total: state.totalInvoices || 0
          },
          incoming: {
            status: statusData.serviceStatus || statusData.status,
            progress: statusData.progress,
            processed: Math.max(statusData.currentInvoiceIndex || 0, statusData.processedCount || 0),
            total: statusData.totalInvoices || 0,
            importSessionId: statusData.importSessionId || statusData.jobId || null
          }
        });
        
        // CRITICAL FIX: Track when the API was called to prevent WebSocket from overriding
        if (statusData._lastApiCallTime) {
          state._lastApiCallTime = statusData._lastApiCallTime;
        }
        const incomingServiceStatus = statusData.serviceStatus || statusData.status;
        state.serviceStatus = {
          status: incomingServiceStatus || state.serviceStatus?.status || 'unknown',
          isRunning: statusData.isRunning === true,
          isConnected: statusData.isConnected === true,
          isHealthy: statusData.isHealthy === true,
          message: statusData.message || state.serviceStatus?.message || '',
          lastActivity: statusData.lastActivity || statusData.lastHeartbeat || state.serviceStatus?.lastActivity || null,
          updatedAt: new Date().toISOString()
        };
        const incomingStatusStage = statusToImportStage(incomingServiceStatus);
        const incomingProgressNumbers = getIncomingProgressNumbers(statusData);
        const activeSessionId = state.activeImportSession?.sessionId || state._importSessionId || null;
        const completedResultSessionId = getPayloadSessionId(statusData.lastImportResult || {}) ||
          getPayloadSessionId(statusData);
        const hasMatchingCompletedResult = Boolean(
          ['idle', 'completed', 'completed_with_errors'].includes(incomingServiceStatus) &&
          hasMeaningfulImportResult(statusData.lastImportResult) &&
          activeSessionId &&
          completedResultSessionId === activeSessionId
        );
        const incomingHasActiveSessionProgress = ACTIVE_IMPORT_STATUSES.includes(incomingStatusStage) &&
          (
            Boolean(getPayloadSessionId(statusData)) ||
            incomingProgressNumbers.total > 0 ||
            incomingProgressNumbers.current > 0 ||
            incomingProgressNumbers.processed > 0 ||
            Number(statusData.progress || 0) > 0
          );
        const shouldPreserveActiveImport = preserveActiveImportFromNonProgressUpdate(state, incomingServiceStatus) &&
          !incomingHasActiveSessionProgress &&
          !hasMatchingCompletedResult;
        
        // Verbose logging removed - status data processed silently
        
        // Handle the new backend structure for remote services
        if (statusData.isRunning !== undefined && statusData.isConnected !== undefined) {
          // This is the new backend structure for remote services
          
          // CRITICAL FIX: Check if there's an active import in progress
          // If so, preserve the connected state even if API returns disconnected
          const progressAgeMs = state._lastProgressAt ? Date.now() - state._lastProgressAt : Infinity;
          const hasFreshProgress = progressAgeMs < STALE_IMPORT_PROGRESS_MS;
          const isActiveImport = (ACTIVE_IMPORT_STATUSES.includes(state.status) || state.isImporting) && hasFreshProgress;
          const hasRecentProgress = state.progress > 0 && state.progress < 100 && hasFreshProgress;
          
          // CRITICAL FIX: Always trust the backend's isConnected status for remote services
          // The backend knows the actual connection status based on heartbeat and activity
          const isUserContextMissing = statusData.message?.includes('user ID not found') || 
                                       statusData.config?.message?.includes('user ID not found') || 
                                       statusData.message?.includes('Authentication required') ||
                                       statusData.message?.includes('No service status found');
          
          // CRITICAL FIX: Log the received status for debugging
          if (process.env.NODE_ENV === 'development') {
              console.log({
              isConnected: statusData.isConnected,
              isRunning: statusData.isRunning,
              isHealthy: statusData.isHealthy,
              serviceStatus: statusData.serviceStatus,
              status: statusData.status,
              message: statusData.message,
              lastActivity: statusData.lastActivity,
              lastHeartbeat: statusData.lastHeartbeat,
              isActiveImport,
              hasRecentProgress,
              currentStateStatus: state.status
            });
          }
          
          if (isUserContextMissing) {
            if (shouldPreserveActiveImport) {
              state.isConnected = true;
              state.isRunning = true;
              state.isRefreshing = false;
              state.isLoading = false;
              state.error = null;
              logImportStateWrite(
                'service_status_poll',
                'preserve_active_import',
                previousState,
                getImportDebugSnapshot(state),
                statusData
              );
              return;
            }
            state.isConnected = false;
            state.isRunning = false;
            state.isHealthy = false;
            state.status = 'error';
            state.message = statusData.message || statusData.config?.message || 'Service not connected';
            state.progress = 0;
            state.isImporting = false;
          } else {
            // CANONICAL LIVENESS: trust the backend top-level health fields. Stale local import
            // context must never fabricate a connected/uploading state. The service is "live" only
            // when the backend reports connected AND running (the backend already flips these to
            // false once its heartbeat TTL/grace expires).
            const secondsSinceHeartbeat = Number.isFinite(Number(statusData.secondsSinceLastActivity))
              ? Number(statusData.secondsSinceLastActivity)
              : null;
            state.secondsSinceLastHeartbeat = secondsSinceHeartbeat;
            state.lastHeartbeat = statusData.lastActivity || statusData.lastHeartbeat || null;
            const serviceIsLive = statusData.isConnected === true && statusData.isRunning === true;

            if (!serviceIsLive) {
              // Service down/stale: never show Connected/Uploading from stale context. Clear any
              // active session and surface the last import as historical only.
              state.isConnected = statusData.isConnected === true;
              state.isRunning = statusData.isRunning === true;
              state.isHealthy = statusData.isHealthy === true;
              state.isImporting = false;
              state.progress = 0;
              state.currentInvoiceIndex = 0;
              state.processedCount = 0;
              state._lastProgressAt = 0;
              state._lastProgressSignature = null;
              if (state.activeImportSession && !TERMINAL_IMPORT_STATUSES.includes(state.activeImportSession.status)) {
                clearActiveImportSession(state, 'service_status_poll', { reason: 'service_not_live', secondsSinceHeartbeat });
              }
              const downStatus = statusData.serviceStatus || statusData.status;
              state.status = (downStatus === 'completed' || downStatus === 'completed_with_errors')
                ? 'idle'
                : (statusData.isConnected === false ? 'disconnected' : 'idle');
              state.message = statusData.message ||
                (state.isConnected ? 'Service connected' : 'Service connection lost');
              if (statusData.lastImportResult && hasMeaningfulImportResult(statusData.lastImportResult)) {
                applyLastImportResult(state, {
                  ...statusData.lastImportResult,
                  timestamp: statusData.lastImportResult.timestamp || Date.now()
                });
              }
              state.isRefreshing = false;
              state.isLoading = false;
              state.error = null;
              logImportStateWrite('service_status_poll', 'service_not_live_cleared', previousState, getImportDebugSnapshot(state), statusData);
              return;
            }

            // Service is live: trust backend connection flags (no stale-context override).
            state.isConnected = statusData.isConnected === true;
            state.isRunning = statusData.isRunning === true;
            state.isHealthy = statusData.isHealthy === true;

            // Determine status based on backend data
            const serviceStatus = incomingServiceStatus;
            if (shouldPreserveActiveImport) {
              state.isConnected = true;
              state.isRunning = true;
              state.isHealthy = statusData.isHealthy === true;
              if (statusData.lastActivity) {
                state.lastCheck = statusData.lastActivity;
              }
              if (statusData.config) {
                state.config = {
                  ...statusData.config,
                  configExists: Boolean(statusData.config.configExists),
                  message: statusData.config.message || (statusData.config.configExists ? "Configuration loaded successfully" : "Configuration not reported"),
                  db_file_path: statusData.config.db_file_path || "Not specified",
                  check_interval: statusData.config.check_interval || "Not specified",
                  api_timeout: statusData.config.api_timeout || "Not specified",
                  long_api_timeout: statusData.config.long_api_timeout || "Not specified",
                  script_directory: statusData.config.script_directory || "Not specified",
                  config_file_path: statusData.config.config_file_path || "Not specified",
                  environment: statusData.config.environment || "Not specified",
                  auto_detect_case: statusData.config.auto_detect_case !== undefined ? statusData.config.auto_detect_case : "Not specified",
                  import_strategy: statusData.config.import_strategy || "Not specified",
                  customer_id: statusData.config.customer_id || "Not specified",
                  customer_name: statusData.config.customer_name || "Not specified",
                  company_username: statusData.config.company_username || "Not specified",
                  service_version: statusData.config.service_version || "1.0.0",
                  python_version: statusData.config.python_version || "Not specified",
                  platform: statusData.config.platform || "Not specified",
                  pypxlib_available: statusData.config.pypxlib_available !== undefined ? statusData.config.pypxlib_available : "Unknown"
                };
              }
              state.serviceVersion = statusData.service_version || state.serviceVersion || null;
              state.isRefreshing = false;
              state.isLoading = false;
              state.error = null;
              logImportStateWrite(
                'service_status_poll',
                'preserve_active_import',
                previousState,
                getImportDebugSnapshot(state),
                statusData
              );
              return;
            }
            
            const previousStatus = state.status;

            // CRITICAL FIX: Map backend status to frontend status
            if (statusData.isConnected !== true && serviceStatus === 'error') {
              state.status = 'disconnected';
            } else if (serviceStatus === 'completed' || serviceStatus === 'completed_with_errors') {
              state.status = 'completed';
            } else if (serviceStatus === 'converting' || serviceStatus === 'uploading' || serviceStatus === 'retrying') {
              state.status = serviceStatus;
            } else if (serviceStatus === 'error') {
              state.status = 'error';
            } else if (serviceStatus === 'idle' || serviceStatus === 'connected') {
              state.status = 'idle';
            } else if (state.isRunning) {
              state.status = 'running';
            } else {
              state.status = serviceStatus || 'unknown';
            }
            
            // Update message - use API message or generate based on status
            state.message = state.status === 'disconnected'
              ? 'Local import agent is offline'
              : statusData.message || (state.isConnected ? 'Service connected and running' : 'Service connection lost');
            
            // CRITICAL FIX: Handle progress based on status
            const isProcessing = ACTIVE_IMPORT_STATUSES.includes(state.status);
            const isCompleted = state.status === 'completed';
            const isIdle = state.status === 'idle';
            
            // Check if completion is fresh (within last 60 seconds). Completed imports
            // from the health endpoint may only carry timestamp/completedAt fields.
            const lastActivity = statusData.lastActivity || statusData.lastHeartbeat;
            const completionTimestamp = statusData.timestamp ||
              statusData.completedAt ||
              statusData.lastImportResult?.completedAt ||
              statusData.lastImportResult?.timestamp ||
              lastActivity;
            const isFreshCompletion = isCompleted && completionTimestamp &&
              ((new Date() - new Date(completionTimestamp)) < 60000);
            const completionTimestampKey = completionTimestamp ? String(completionTimestamp) : null;
            const completionAlreadyDisplayed = completionTimestampKey &&
              state._lastDisplayedCompletionTimestamp === completionTimestampKey;
            const isNoNewDataCompletion = isNoNewDataImportMessage(statusData.message);
            
            if (isProcessing) {
              // If service restarted with a new importSessionId, clear the stale old session
              // so the new session can take over. Guard: service must be confirmed live.
              const existingActiveSessionId = state.activeImportSession?.sessionId;
              const incomingActiveSessionId = getPayloadSessionId(statusData);
              const serviceIsLive = statusData.isConnected === true && statusData.isRunning === true;
              if (existingActiveSessionId && incomingActiveSessionId &&
                  existingActiveSessionId !== incomingActiveSessionId && serviceIsLive) {
                state.activeImportSession = null;
                state._importSessionId = null;
              }

              // Health polling can lag behind detailed progress events. Keep active
              // imports monotonic so a coarse heartbeat cannot move the bar backward.
              const acceptedStatusProgress = upsertActiveImportSessionFromProgress(
                state,
                {
                  ...statusData,
                  status: state.status,
                  timestamp: statusData.timestamp || statusData.lastActivity || statusData.lastHeartbeat || new Date().toISOString()
                },
                'service_status_poll'
              );
              if (!acceptedStatusProgress) {
                state.isConnected = true;
                state.isRunning = true;
                state.isImporting = true;
                state.isRefreshing = false;
                state.isLoading = false;
                state.error = null;
                logImportStateWrite(
                  'service_status_poll',
                  'processing_progress_rejected_stale_or_batch_update',
                  previousState,
                  getImportDebugSnapshot(state),
                  statusData
                );
                return;
              }

              const totalInvoices = statusData.totalInvoices || state.totalInvoices || 0;
              const incomingCurrentIndex = statusData.currentInvoiceIndex || 0;
              const incomingProcessedCount = statusData.processedCount || 0;
              const processedInvoices = Math.max(
                incomingCurrentIndex || state.currentInvoiceIndex || 0,
                incomingProcessedCount || state.processedCount || 0
              );
              const reachedInvoiceEnd = totalInvoices > 0 && processedInvoices >= totalInvoices;
              const progressSignature = getProgressSignature(statusData);
              const isEndpointOnlyFinalizingSnapshot = reachedInvoiceEnd &&
                (incomingCurrentIndex >= totalInvoices || incomingProcessedCount >= totalInvoices);

              if (!isEndpointOnlyFinalizingSnapshot && progressSignature !== state._lastProgressSignature) {
                state._lastProgressSignature = progressSignature;
                state._lastProgressAt = Date.now();
              }
              const updatedProgressAgeMs = state._lastProgressAt ? Date.now() - state._lastProgressAt : Infinity;
              const isStaleFinalizingProgress = reachedInvoiceEnd &&
                (isEndpointOnlyFinalizingSnapshot || updatedProgressAgeMs > STALE_IMPORT_PROGRESS_MS);

              if (isStaleFinalizingProgress) {
                state.status = 'idle';
                state.progress = 0;
                state.currentInvoiceIndex = 0;
                state.processedCount = 0;
                state._lastProgressAt = 0;
                state._lastProgressSignature = null;
                state.isImporting = false;
                state.showImportNotification = false;
                state.message = 'Last import progress stopped before completion. Waiting for the next update from the local agent.';
                setLatestScan(state, 'Import progress stopped before completion. Check the local Auto-Import GUI log for the latest result.');
              } else {
                state.progress = Math.max(state.progress || 0, state.activeImportSession?.progress || 0, reachedInvoiceEnd ? 99 : 0);
                state.totalInvoices = Math.max(Number(state.totalInvoices || 0), Number(state.activeImportSession?.total || statusData.totalInvoices || 0));
                state.currentInvoiceIndex = Math.max(Number(state.currentInvoiceIndex || 0), Number(state.activeImportSession?.processed || statusData.currentInvoiceIndex || 0));
                state.processedCount = Math.max(Number(state.processedCount || 0), Number(state.activeImportSession?.processed || statusData.processedCount || 0));
                state.isImporting = true;
              }
            } else if (isFreshCompletion && completionAlreadyDisplayed && previousStatus === 'idle') {
              state.status = 'idle';
              state.progress = 0;
              state.showImportNotification = false;
              state.isImporting = false;
              if (state.activeImportSession) {
                clearActiveImportSession(state, 'service_status_poll', { reason: 'completion_already_displayed_idle' });
              }
            } else if (isFreshCompletion) {
              if (isNoNewDataCompletion) {
                state.progress = 0;
                state.status = 'idle';
                state.showImportNotification = false;
                state.isImporting = false;
                state.message = statusData.message || 'No changes detected in database file';
                setLatestScan(state, 'No new changes detected since the last scan.');
                state.showLastImportResult = hasMeaningfulImportResult(state.lastImportResult);
                if (state.activeImportSession) {
                  clearActiveImportSession(state, 'service_status_poll', { reason: 'no_new_data_completion' });
                }
              } else {
                // Show 100% only for fresh completions
                if (completionTimestampKey) {
                  state._lastDisplayedCompletionTimestamp = completionTimestampKey;
                }
                state.progress = 100;
                state.showImportNotification = true;
                state.isImporting = false;
                const countSummary = getImportCountSummary(statusData, {
                  terminal: true,
                  previousTotal: Number(state.totalInvoices || 0),
                  previousProcessed: Number(state.processedCount || 0)
                });
                state.totalInvoices = countSummary.total;
                state.processedCount = countSummary.processed;
                state.successCount = Math.max(Number(state.successCount || 0), Number(statusData.successCount || 0));
                state.failedCount = Math.max(Number(state.failedCount || 0), Number(statusData.failedCount || 0));
                state.message = statusData.lastImportResult?.message || statusData.message || 'Import completed';
                state.lastImportResult = {
                  successCount: statusData.successCount || 0,
                  failedCount: statusData.failedCount || 0,
                  totalInvoices: countSummary.total,
                  processedCount: countSummary.processed,
                  createdCount: statusData.createdCount || 0,
                  updatedCount: statusData.updatedCount || 0,
                  unchangedCount: statusData.unchangedCount || 0,
                  protectedCount: statusData.protectedCount || 0,
                  conflictCount: statusData.conflictCount || 0,
                  sourceChangedCount: statusData.sourceChangedCount || 0,
                  skippedCount: statusData.skippedCount || 0,
                  alreadySubmittedCount: statusData.alreadySubmittedCount || 0,
                  skippedReason: statusData.skippedReason || null,
                  skippedReasonMessage: statusData.skippedReasonMessage || null,
                  validationFailedCount: statusData.validationFailedCount || 0,
                  failedRecords: statusData.failedRecords || [],
                  results: statusData.results || [],
                  noNewData: isNoNewDataCompletion,
                  dataSync: String(statusData.message || '').toLowerCase().includes('already synchronized'),
                  message: statusData.lastImportResult?.message || statusData.message || 'Import completed',
                  timestamp: completionTimestamp
                };
                state.showLastImportResult = true;
                state.latestScanMessage = null;
                state.latestScanAt = null;
                // Finalize a lingering active session via polling when the WS completion was missed,
                // so the progress card shows 100%/Completed instead of sticking at the last progress.
                if (state.activeImportSession &&
                    !['completed', 'completed_with_errors', 'error', 'failed', 'cancelled'].includes(state.activeImportSession.status)) {
                  completeActiveImportSession(state, statusData, 'completed', 'service_status_poll_completion');
                }
              }
            } else if (isCompleted && !isFreshCompletion) {
              // Stale completion - treat as idle, hide progress
              state.progress = 0;
              state.status = 'idle';
              state.showImportNotification = false;
              state.isImporting = false;
              if (state.activeImportSession) {
                clearActiveImportSession(state, 'service_status_poll', { reason: 'stale_completion' });
              }
            } else if (isIdle) {
              // Reset to 0 for idle states
              state.progress = 0;
              state.showImportNotification = false;
              state.isImporting = false;
              // Backend reports idle => no active import. Clear any lingering session so the progress
              // card disappears even when the WS completion event was missed (polling-only finalize).
              if (state.activeImportSession) {
                clearActiveImportSession(state, 'service_status_poll', { reason: 'import_finalized_idle' });
              }
            }

            if (statusData.lastImportResult) {
              applyLastImportResult(state, statusData.lastImportResult);
            }
          }

          
          if (statusData.lastActivity) {
            state.lastCheck = statusData.lastActivity;
          }

          // Save the config data with enhanced handling
          if (statusData.config) {
            state.config = {
              ...statusData.config,
              // Only require configExists, other fields are optional with good defaults
              configExists: Boolean(statusData.config.configExists),
              // Provide meaningful fallback message
              message: statusData.config.message || (statusData.config.configExists ? "Configuration loaded successfully" : "Configuration not reported"),
              // Ensure other fields have defaults if missing
              db_file_path: statusData.config.db_file_path || "Not specified",
              check_interval: statusData.config.check_interval || "Not specified",
              api_timeout: statusData.config.api_timeout || "Not specified",
              long_api_timeout: statusData.config.long_api_timeout || "Not specified",
              script_directory: statusData.config.script_directory || "Not specified",
              config_file_path: statusData.config.config_file_path || "Not specified",
              environment: statusData.config.environment || "Not specified",
              auto_detect_case: statusData.config.auto_detect_case !== undefined ? statusData.config.auto_detect_case : "Not specified",
              import_strategy: statusData.config.import_strategy || "Not specified",
              customer_id: statusData.config.customer_id || "Not specified",
              customer_name: statusData.config.customer_name || "Not specified",
              company_username: statusData.config.company_username || "Not specified",
              service_version: statusData.config.service_version || "1.0.0",
              python_version: statusData.config.python_version || "Not specified",
              platform: statusData.config.platform || "Not specified",
              pypxlib_available: statusData.config.pypxlib_available !== undefined ? statusData.config.pypxlib_available : "Unknown"
            };
          } else {
            // If no config provided, create a default config object
            state.config = { 
              configExists: false, 
              message: "Configuration not reported by service",
              db_file_path: "Not specified",
              check_interval: "Not specified",
              api_timeout: "Not specified",
              long_api_timeout: "Not specified",
              script_directory: "Not specified",
              config_file_path: "Not specified",
              environment: "Not specified",
              auto_detect_case: "Not specified",
              import_strategy: "Not specified",
              customer_id: "Not specified",
              customer_name: "Not specified",
              company_username: "Not specified",
              service_version: "1.0.0",
              python_version: "Not specified",
              platform: "Not specified",
              pypxlib_available: "Unknown"
            };
          }
          
          // Set other fields to maintain compatibility
          if (![...ACTIVE_IMPORT_STATUSES, 'completed', 'completed_with_errors'].includes(state.status)) {
            state.progress = 0;
          }
          state.lastProcessedHash = null;
          state.lastFileModTime = null;
          state.processedInvoices = [];
          state.serviceVersion = statusData.service_version || null;
          
          // Status updated silently to reduce console noise

          
        } else {
          // Legacy structure - use the existing logic
          // Processing local service status silently

          if (shouldPreserveActiveImport) {
            state.isRefreshing = false;
            state.isLoading = false;
            state.error = null;
            if (statusData.last_check) {
              state.lastCheck = statusData.last_check;
            }
            state.serviceVersion = statusData.service_version || state.serviceVersion;
            logImportStateWrite(
              'service_status_poll',
              'preserve_active_import_legacy',
              previousState,
              getImportDebugSnapshot(state),
              statusData
            );
            return;
          }

          // Set basic fields from legacy structure
          state.status = statusData.status || 'unknown';
          state.progress = statusData.progress || 0;
          state.message = statusData.message || '';
          state.lastProcessedHash = statusData.last_processed_hash;
          state.lastFileModTime = statusData.last_file_mod_time;
          state.processedInvoices = statusData.processed_invoices || [];
          state.lastCheck = statusData.last_check;
          state.serviceVersion = statusData.service_version;
          
          // CRITICAL FIX: Simplified connection status logic for legacy structure
          const message = statusData.message || '';
          const status = statusData.status || '';
          
          // Legacy status data processed silently


          // Check if status was recently updated (align with backend 10-minute window)
          let statusRecentlyUpdated = false;
          if (statusData.last_updated) {
            try {
              const lastUpdated = new Date(statusData.last_updated);
              const now = new Date();
              const diffMinutes = (now - lastUpdated) / (1000 * 60);
              statusRecentlyUpdated = diffMinutes <= 10;
          // Status recency check processed silently
            } catch (e) {
              // Silently handle date parsing errors
            }
          }


          const isUserContextMissing = statusData.message?.includes('user ID not found') || statusData.message?.includes('Authentication required');

          if (isUserContextMissing) {
            state.isConnected = false;
            state.status = 'error';
            state.message = `Setup required: ${statusData.message}. Client will stop polling for service status.`;
          // Forcing disconnected state due to missing user context
            return; // Exit reducer early after setting state for this case
          }


          // Check if we have recent activity indicators (align with backend logic)
          const hasRecentActivity = statusRecentlyUpdated ||
                                    (statusData.last_check &&
                                    (new Date() - new Date(statusData.last_check)) / (1000 * 60) <= 10);

          // Service is connected if:
          // 1. Status is not explicitly disconnected
          // 2. AND we have recent activity (within 10 minutes, matching backend)
          // 3. OR status indicates active operation
          const isDisconnectedByStatus = status === 'disconnected';
          const isActiveStatus = [...ACTIVE_IMPORT_STATUSES, 'completed', 'completed_with_errors', 'idle'].includes(status);

          const isConnected = !isDisconnectedByStatus && (hasRecentActivity || isActiveStatus);

          // Connection status determination processed silently


          state.isConnected = isConnected;
        }
        
        state.isRefreshing = false;
        state.isLoading = false; // NEW: Clear loading state after status check
        state.error = null;
        logImportStateWrite(
          'service_status_poll',
          'fulfilled',
          previousState,
          getImportDebugSnapshot(state),
          statusData
        );
      })
      .addCase(getAutoImportStatus.rejected, (state, action) => {
        const previousState = getImportDebugSnapshot(state);
        state.isRefreshing = false;
        state.isLoading = false; // Clear loading state even on error
        state.error = action.payload;

        // A failed health poll must NEVER show Connected. Mark disconnected and clear any
        // non-terminal active session so a stale "Uploading" card cannot persist; a later
        // successful poll or WebSocket progress event re-creates the session if truly live.
        state.isConnected = false;
        state.isRunning = false;
        state.isHealthy = false;
        state.status = 'disconnected';
        state.message = action.payload || 'Auto-import service not connected';
        state.progress = 0;
        state.isImporting = false;
        state._lastProgressAt = 0;
        state._lastProgressSignature = null;
        if (state.activeImportSession && !TERMINAL_IMPORT_STATUSES.includes(state.activeImportSession.status)) {
          clearActiveImportSession(state, 'service_status_poll', { reason: 'health_poll_rejected' });
        }
        logImportStateWrite(
          'service_status_poll',
          'rejected',
          previousState,
          getImportDebugSnapshot(state),
          action.payload
        );
      })
      // Trigger import
      .addCase(triggerAutoImport.pending, (state) => {
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(triggerAutoImport.fulfilled, (state, action) => {
        state.isRefreshing = false;
        state.error = null;
        state.showImportNotification = true;
        state.status = 'triggered';
        state.message = 'Manual import triggered';
      })
      .addCase(triggerAutoImport.rejected, (state, action) => {
        state.isRefreshing = false;
        state.isImporting = false;
        state.error = action.payload;
      })
      // Update status
      .addCase(updateAutoImportStatus.pending, (state) => {
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(updateAutoImportStatus.fulfilled, (state, action) => {
        const statusData = action.payload;
        const incomingServiceStatus = statusData.serviceStatus || statusData.status;
        const incomingStatusStage = statusToImportStage(incomingServiceStatus);
        const incomingProgressNumbers = getIncomingProgressNumbers(statusData);
        const incomingHasActiveSessionProgress = ACTIVE_IMPORT_STATUSES.includes(incomingStatusStage) &&
          (
            Boolean(getPayloadSessionId(statusData)) ||
            incomingProgressNumbers.total > 0 ||
            incomingProgressNumbers.current > 0 ||
            incomingProgressNumbers.processed > 0 ||
            Number(statusData.progress || 0) > 0
          );
        const shouldPreserveActiveImport = preserveActiveImportFromNonProgressUpdate(state, incomingServiceStatus) &&
          !incomingHasActiveSessionProgress;
        
        // Verbose logging removed - status update processed silently
        if (incomingHasActiveSessionProgress) {
          const acceptedStatusProgress = upsertActiveImportSessionFromProgress(
            state,
            {
              ...statusData,
              status: incomingStatusStage,
              timestamp: statusData.timestamp || statusData.lastActivity || statusData.lastHeartbeat || new Date().toISOString()
            },
            'service_status_update'
          );

          if (acceptedStatusProgress) {
            state.status = incomingStatusStage;
            state.progress = Math.max(Number(state.progress || 0), Number(state.activeImportSession?.progress || 0));
            state.totalInvoices = Math.max(Number(state.totalInvoices || 0), Number(state.activeImportSession?.total || 0));
            state.currentInvoiceIndex = Math.max(Number(state.currentInvoiceIndex || 0), Number(state.activeImportSession?.processed || 0));
            state.processedCount = Math.max(Number(state.processedCount || 0), Number(state.activeImportSession?.processed || 0));
            state.isConnected = true;
            state.isRunning = true;
            state.isImporting = true;
            state.showImportNotification = true;
            state.showLastImportResult = false;
            state.latestScanMessage = null;
            state.latestScanAt = null;
            state.message = statusData.message || state.activeImportSession?.message || state.message;
            state.lastCheck = statusData.lastActivity || statusData.lastHeartbeat || statusData.timestamp || new Date().toISOString();
            state.isRefreshing = false;
            state.error = null;
            return;
          }
        }
        
        // Handle the new backend structure for remote services
        if (statusData.isRunning !== undefined && statusData.isConnected !== undefined) {
          if (shouldPreserveActiveImport) {
            state.isRefreshing = false;
            state.error = null;
            state.isConnected = true;
            state.isRunning = true;
            if (statusData.lastActivity) {
              state.lastCheck = statusData.lastActivity;
            }
            if (statusData.config) {
              state.config = statusData.config;
            }
            return;
          }
          // This is the new backend structure for remote services
          // Processing remote service status update silently


          // For remote services, use the backend's isConnected status directly
          state.isConnected = statusData.isConnected;
          state.isRunning = statusData.isRunning; // Set isRunning field from backend
          state.status = statusData.isRunning ? 'running' : 'stopped';
          state.message = statusData.config?.message || statusData.message || 'Remote service monitoring...';
          
          if (statusData.lastActivity) {
            state.lastCheck = statusData.lastActivity;
          }

          // Save the config data
          state.config = statusData.config || null;
          
          // Set other fields to maintain compatibility
          state.progress = 0;
          state.lastProcessedHash = null;
          state.lastFileModTime = null;
          state.processedInvoices = [];
          state.serviceVersion = statusData.service_version || null;
          
        } else {
          // Legacy structure - use the existing logic
          // Processing local service status update silently

          if (shouldPreserveActiveImport) {
            state.isRefreshing = false;
            state.error = null;
            if (statusData.last_check) {
              state.lastCheck = statusData.last_check;
            }
            state.serviceVersion = statusData.service_version || state.serviceVersion;
            return;
          }

          // Set basic fields from legacy structure
          state.status = statusData.status || 'unknown';
          state.progress = statusData.progress || 0;
          state.message = statusData.message || '';
          state.lastProcessedHash = statusData.last_processed_hash;
          state.lastFileModTime = statusData.last_file_mod_time;
          state.processedInvoices = statusData.processed_invoices || [];
          state.lastCheck = statusData.last_check;
          state.serviceVersion = statusData.service_version;
          
          // Determine connection status - SIMPLIFIED AND IMPROVED LOGIC
          const message = statusData.message || '';
          const status = statusData.status || '';
          
          // Legacy update status data processed silently


          // Check if status was recently updated (align with backend 10-minute window)
          let statusRecentlyUpdated = false;
          if (statusData.last_updated) {
            try {
              const lastUpdated = new Date(statusData.last_updated);
              const now = new Date();
              const diffMinutes = (now - lastUpdated) / (1000 * 60);
              statusRecentlyUpdated = diffMinutes <= 10;
          // Status recency check processed silently
            } catch (e) {
              // Silently handle date parsing errors
            }
          }


          // Check if we have recent activity indicators (align with backend logic)
          const hasRecentActivity = statusRecentlyUpdated || 
                                   (statusData.last_check && 
                                    (new Date() - new Date(statusData.last_check)) / (1000 * 60) <= 10);

          // Service is connected if:
          // 1. Status is not explicitly disconnected
          // 2. AND we have recent activity (within 10 minutes, matching backend)
          // 3. OR status indicates active operation
          const isDisconnectedByStatus = status === 'disconnected';
          const isActiveStatus = [...ACTIVE_IMPORT_STATUSES, 'completed', 'completed_with_errors', 'idle'].includes(status);
          
          const isConnected = !isDisconnectedByStatus && (hasRecentActivity || isActiveStatus);
          
          // Connection status determination processed silently


          state.isConnected = isConnected;
        }
        
        state.isRefreshing = false;
        state.error = null;
      })
      .addCase(updateAutoImportStatus.rejected, (state, action) => {
        state.isRefreshing = false;
        state.error = action.payload;
      })
      // NEW: Execute manual import
      .addCase(executeManualImport.pending, (state) => {
        state.isImporting = true;
        state.error = null;
        state.importStartTime = new Date().toISOString();
        state.progress = 0;
        // CRITICAL FIX: Reset the shown flag for new import session
        state.hasShownImportResultForCurrentImport = false;
      })
      .addCase(executeManualImport.fulfilled, (state, action) => {
        const { data } = action.payload;
        
        state.isImporting = false;
        state.importJobId = data.jobId;
        state.importType = data.importType;
        state.totalInvoices = data.totalInvoices || 0;
        state.processedCount = data.processedCount || 0;
        state.successCount = data.successCount || 0;
        state.failedCount = data.failedCount || 0;
        state.failedInvoices = data.failedInvoices || [];
        state.importResults = data.results || [];
        state.importDuration = data.duration;
        state.importEndTime = new Date().toISOString();
        state.progress = 100;
        
        // Show result modal on completion
        if (data.serviceConnected) {
          // If using Python service, we'll wait for WebSocket updates
          state.message = 'Manual import triggered via service';
        } else {
          // Direct import completed
          state.showImportResultModal = true;
          state.message = `Import completed: ${data.successCount} success, ${data.failedCount} failed`;
        }
      })
      .addCase(executeManualImport.rejected, (state, action) => {
        state.isImporting = false;
        state.error = action.payload;
        state.importEndTime = new Date().toISOString();
        state.message = `Import failed: ${action.payload}`;
      })
      // NEW: Get import progress
      .addCase(getImportProgress.pending, (state) => {
        state.isRefreshing = true;
      })
      .addCase(getImportProgress.fulfilled, (state, action) => {
        const previousState = getImportDebugSnapshot(state);
        const { data } = action.payload;
        
        state.isRefreshing = false;
        state.importJobId = data.jobId;
        state.importType = data.importType;
        state.totalInvoices = data.totalInvoices || 0;
        state.currentInvoiceIndex = data.currentInvoiceIndex || 0;
        state.currentInvoiceNumber = data.currentInvoiceNumber;
        state.processedCount = data.processedCount || 0;
        state.successCount = data.successCount || 0;
        state.failedCount = data.failedCount || 0;
        state.failedInvoices = data.failedInvoices || [];
        state.progress = data.percentage || 0;
        state.importStartTime = data.startTime;
        state.importDuration = data.duration;
        state.message = data.message;
        
        // Check if import is complete
        const isComplete = data.status === 'completed' || data.status === 'completed_with_errors';
        if (isComplete && state.isImporting) {
          state.isImporting = false;
          state.importEndTime = new Date().toISOString();
          state.showImportResultModal = true;
        }
        logImportStateWrite(
          'dashboard_refresh',
          'getImportProgress_fulfilled',
          previousState,
          getImportDebugSnapshot(state),
          data
        );
      })
      .addCase(getImportProgress.rejected, (state, action) => {
        const previousState = getImportDebugSnapshot(state);
        state.isRefreshing = false;
        if (!hasLockedImportProgress(state)) {
          state.isImporting = false;
        }
        state.error = action.payload;
        logImportStateWrite(
          'dashboard_refresh',
          'getImportProgress_rejected',
          previousState,
          getImportDebugSnapshot(state),
          action.payload
        );
      });
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setShowImportNotification: (state, action) => {
      state.showImportNotification = action.payload;
    },
    setLastStatsRefresh: (state, action) => {
      state.lastStatsRefresh = action.payload;
    },
    resetAutoImportState: (state) => {
      return initialState;
    },
    setIsConnected: (state, action) => {
      state.isConnected = action.payload;
    },
    setProgress: (state, action) => {
      const previousState = getImportDebugSnapshot(state);
      const incomingProgress = Number(action.payload || 0);
      if (hasLockedImportProgress(state) && incomingProgress < Number(state.progress || 0)) {
        logImportStateWrite(
          'dashboard_or_service_action',
          'setProgress_ignored_backward_active_import',
          previousState,
          getImportDebugSnapshot(state),
          action.payload
        );
        return;
      }
      state.progress = action.payload;
      logImportStateWrite(
        'dashboard_or_service_action',
        'setProgress',
        previousState,
        getImportDebugSnapshot(state),
        action.payload
      );
    },
    setStatus: (state, action) => {
      const previousState = getImportDebugSnapshot(state);
      const incomingStatus = action.payload;
      if (
        hasLockedImportProgress(state) &&
        !ACTIVE_IMPORT_STATUSES.includes(incomingStatus) &&
        !TERMINAL_IMPORT_STATUSES.includes(incomingStatus)
      ) {
        logImportStateWrite(
          'dashboard_or_service_action',
          'setStatus_ignored_active_import',
          previousState,
          getImportDebugSnapshot(state),
          incomingStatus
        );
        return;
      }
      state.status = incomingStatus;
      logImportStateWrite(
        'dashboard_or_service_action',
        'setStatus',
        previousState,
        getImportDebugSnapshot(state),
        incomingStatus
      );
    },
    setMessage: (state, action) => {
      state.message = action.payload;
    },
    // NEW: Set loading state for initial status check
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    // NEW: Actions for manual import progress tracking
    setImportJobId: (state, action) => {
      state.importJobId = action.payload;
    },
    setIsImporting: (state, action) => {
      const previousState = getImportDebugSnapshot(state);
      if (hasLockedImportProgress(state) && action.payload === false && ACTIVE_IMPORT_STATUSES.includes(state.status)) {
        logImportStateWrite(
          'dashboard_or_service_action',
          'setIsImporting_false_ignored_active_import',
          previousState,
          getImportDebugSnapshot(state),
          action.payload
        );
        return;
      }
      state.isImporting = action.payload;
      logImportStateWrite(
        'dashboard_or_service_action',
        'setIsImporting',
        previousState,
        getImportDebugSnapshot(state),
        action.payload
      );
    },
    setShowImportResultModal: (state, action) => {
      state.showImportResultModal = action.payload;
    },
    updateImportProgress: (state, action) => {
      const { currentIndex, total, currentInvoiceNumber, percentage } = action.payload;
      state.currentInvoiceIndex = currentIndex;
      state.totalInvoices = total;
      state.currentInvoiceNumber = currentInvoiceNumber;
      state.progress = percentage;
    },
    setImportResults: (state, action) => {
      const previousState = getImportDebugSnapshot(state);
      const results = action.payload || {};
      const noNewData = results.noNewData === true ||
        results.dataSync === true ||
        isNoNewDataImportMessage(results.message);
      state.importResults = results;
      state.importEndTime = new Date().toISOString();

      if (hasLockedImportProgress(state)) {
        state.showLastImportResult = false;
        logImportStateWrite(
          'last_import_refresh',
          'setImportResults_ignored_active_import',
          previousState,
          getImportDebugSnapshot(state),
          results
        );
        return;
      }

      state.isImporting = false;

      if (noNewData) {
        state.progress = 0;
        state.status = 'idle';
        state.showImportNotification = false;
        state.lastImportResult = {
          ...(state.lastImportResult || {}),
          ...results,
          noNewData: true
        };
        state.showLastImportResult = hasMeaningfulImportResult(state.lastImportResult);
        setLatestScan(state, 'No new changes detected since the last scan.');
        if (results.message) {
          state.message = results.message;
        }
        logImportStateWrite(
          'last_import_refresh',
          'setImportResults_no_new_data',
          previousState,
          getImportDebugSnapshot(state),
          results
        );
        return;
      }
      
      state.failedInvoices = results.failedInvoices || state.failedInvoices || [];
      state.lastImportResult = {
        ...(state.lastImportResult || {}),
        ...results
      };
      state.showLastImportResult = true;
      state.latestScanMessage = null;
      state.latestScanAt = null;
      
      // CRITICAL FIX: Always show results in the card, never in a modal
      state.showImportResultModal = false;
      
      // Update message if provided
      if (results.message) {
        state.message = results.message;
      }
      logImportStateWrite(
        'last_import_refresh',
        'setImportResults',
        previousState,
        getImportDebugSnapshot(state),
        results
      );
    },
    resetImportState: (state) => {
      state.importJobId = null;
      state.importType = null;
      state.totalInvoices = 0;
      state.currentInvoiceIndex = 0;
      state.currentInvoiceNumber = null;
      state.processedCount = 0;
      state._lastProgressAt = 0;
      state._lastProgressSignature = null;
      state.successCount = 0;
      state.failedCount = 0;
      state.failedInvoices = [];
      state.importStartTime = null;
      state.importEndTime = null;
      state.importDuration = null;
      state.importResults = null;
      state.isImporting = false;
      state.showImportResultModal = false;
      state.progress = 0;
    },
    // NEW: Handle detailed import progress from WebSocket
    handleDetailedImportProgress: (state, action) => {
      const previousState = getImportDebugSnapshot(state);
      const payload = action.payload || {};
      console.info('[AUTO_IMPORT_PROGRESS_READ]', {
        source: 'FRONTEND_WEBSOCKET_PROGRESS',
        timestamp: new Date().toISOString(),
        previous: {
          status: state.status,
          progress: state.progress,
          processed: Math.max(state.currentInvoiceIndex || 0, state.processedCount || 0),
          total: state.totalInvoices || 0
        },
        incoming: {
          status: payload.status,
          progress: payload.percentage ?? payload.progress,
          processed: Math.max(payload.currentInvoiceIndex || payload.current_index || 0, payload.processedCount || payload.processed_count || 0),
          total: payload.totalInvoices ?? payload.total_invoices ?? 0,
          importSessionId: payload.importSessionId || payload.jobId || null
        }
      });
      
      // CRITICAL FIX: Detect new import session indicators
      const prevTotalInvoices = state.totalInvoices || 0;
      const prevCurrentIndex = state.currentInvoiceIndex || 0;
      const prevProgress = state.progress || 0;
      
      const { total: incomingTotal, current: incomingCurrent, processed: incomingProcessed } = getIncomingProgressNumbers(payload);
      const activeBeforeProgress = hasActiveImportSession(state);
      const incomingLooksLikeBatchCounter = activeBeforeProgress &&
        prevTotalInvoices > 0 &&
        incomingTotal > 0 &&
        incomingTotal < prevTotalInvoices;
      const newTotalInvoices = incomingLooksLikeBatchCounter ? prevTotalInvoices : incomingTotal;
      const newCurrentIndex = incomingLooksLikeBatchCounter
        ? Math.max(prevCurrentIndex, state.processedCount || 0)
        : incomingCurrent;
      const newProgress = payload.percentage ?? payload.progress ?? 0;
      const newProcessedCount = incomingLooksLikeBatchCounter
        ? Math.max(state.processedCount || 0, prevCurrentIndex || 0)
        : incomingProcessed;
      const hasReachedInvoiceEnd = newTotalInvoices > 0 &&
        Math.max(newCurrentIndex || 0, newProcessedCount || 0) >= newTotalInvoices;

      // If new service session detected via WS, clear stale old session (WS events only arrive from live service)
      const existingWsSessionId = state.activeImportSession?.sessionId;
      const incomingWsSessionId = getPayloadSessionId(payload);
      if (existingWsSessionId && incomingWsSessionId && existingWsSessionId !== incomingWsSessionId) {
        state.activeImportSession = null;
        state._importSessionId = null;
      }

      const acceptedProgressUpdate = upsertActiveImportSessionFromProgress(state, payload, 'websocket_progress');

      if (!acceptedProgressUpdate) {
        state.isConnected = true;
        state.isRunning = true;
        state.isRefreshing = false;
        state.error = null;
        logImportStateWrite(
          'websocket',
          'handleDetailedImportProgress_rejected_stale_or_batch_update',
          previousState,
          getImportDebugSnapshot(state),
          payload
        );
        return;
      }

      const acceptedSession = state.activeImportSession;
      
      // Detect new import session:
      // 1. totalInvoices changed from 0 to positive
      // 2. totalInvoices changed to a different positive value
      // 3. currentIndex reset to 0 from a higher value
      // 4. Progress dropped significantly (more than 50%)
      const isNewSession = (
        !activeBeforeProgress && (
          (prevTotalInvoices === 0 && newTotalInvoices > 0) ||
          (prevTotalInvoices > 0 && newTotalInvoices > 0 && prevTotalInvoices !== newTotalInvoices) ||
          (prevCurrentIndex > 0 && newCurrentIndex === 0) ||
          (newProgress < prevProgress - 50)
        )
      );
      
      // CRITICAL FIX: Log progress updates for debugging (only in development)
      if (process.env.NODE_ENV === 'development') {
          console.log({
          isNewSession,
          newSessionIndicators: {
            totalInvoicesZeroToPositive: prevTotalInvoices === 0 && newTotalInvoices > 0,
            totalInvoicesChanged: prevTotalInvoices > 0 && newTotalInvoices > 0 && prevTotalInvoices !== newTotalInvoices,
            indexReset: prevCurrentIndex > 0 && newCurrentIndex === 0,
            progressDropped: newProgress < prevProgress - 50
          },
          prevValues: {
            totalInvoices: prevTotalInvoices,
            currentIndex: prevCurrentIndex,
            progress: prevProgress
          },
          newValues: {
            totalInvoices: newTotalInvoices,
            currentIndex: newCurrentIndex,
            progress: newProgress
          },
          percentage: payload.percentage,
          progress: payload.progress,
          currentInvoiceIndex: payload.currentInvoiceIndex ?? payload.current_index,
          totalInvoices: payload.totalInvoices ?? payload.total_invoices,
          status: payload.status,
          message: payload.message
        });
      }
      
      // CRITICAL FIX: Reset progress for new import session
      if (isNewSession && !acceptedSession?.sessionId) {
        state.progress = 0; // Reset progress for new session
        state.processedCount = 0;
        state._lastProgressAt = 0;
        state._lastProgressSignature = null;
        state.successCount = 0;
        state.failedCount = 0;
        state.currentInvoiceIndex = 0;
      }
      
      // CRITICAL FIX: Always update connection status when receiving progress
      // This ensures frontend knows service is active
      state.isConnected = true;
      state.isRunning = true;
      
      // CRITICAL FIX: Handle both camelCase and snake_case field names from backend
      // Use nullish coalescing (??) to properly handle 0 values
      if (payload.totalInvoices !== undefined || payload.total_invoices !== undefined) {
        state.totalInvoices = Number(acceptedSession?.total || newTotalInvoices || 0);
      }
      if (payload.currentInvoiceIndex !== undefined || payload.current_index !== undefined) {
        state.currentInvoiceIndex = Number(acceptedSession?.processed || Math.min(newTotalInvoices || newCurrentIndex, newCurrentIndex));
      }
      if (payload.currentInvoiceNumber !== undefined || payload.current_invoice_number !== undefined) {
        state.currentInvoiceNumber = payload.currentInvoiceNumber ?? payload.current_invoice_number ?? state.currentInvoiceNumber ?? null;
      }
      if (payload.processedCount !== undefined || payload.processed_count !== undefined) {
        state.processedCount = Number(acceptedSession?.processed || Math.min(newTotalInvoices || newProcessedCount, newProcessedCount));
      }
      if (payload.successCount !== undefined || payload.success_count !== undefined) {
        state.successCount = payload.successCount ?? payload.success_count ?? 0;
      }
      if (payload.failedCount !== undefined || payload.failed_count !== undefined) {
        state.failedCount = payload.failedCount ?? payload.failed_count ?? 0;
      }
      
      // CRITICAL FIX: Handle percentage field from backend (smooth progress)
      // For new sessions, use the new progress directly. For existing sessions, prevent backward progress.
      let finalProgress = Number(acceptedSession?.progress || 0);
      
      // CRITICAL FIX: Prevent premature 100% - cap at 99% unless status is completed
      // This prevents the progress bar from showing 100% before the import is actually done
      if (finalProgress >= 100 && payload.status !== 'completed' && payload.status !== 'completed_with_errors') {
        finalProgress = 99;
      }
      if (hasReachedInvoiceEnd && payload.status !== 'completed' && payload.status !== 'completed_with_errors') {
        finalProgress = Math.max(finalProgress, 99);
      }
      
      state.progress = finalProgress;
      
      if (payload.status) {
        state.status = payload.status;
      }
      if (payload.message) {
        state.message = payload.message;
      }
      if (payload.failedInvoices) {
        state.failedInvoices = payload.failedInvoices;
      }

      state.lastCheck = payload.timestamp || new Date().toISOString();
      
      // Show notification during active import
      if (ACTIVE_IMPORT_STATUSES.includes(payload.status) || newProgress > 0) {
        state.showImportNotification = true;
        state.isImporting = true;
        state.isManuallyTriggered = true; // Mark as manually triggered when we see progress
        state.showLastImportResult = false;
        state.latestScanMessage = null;
        state.latestScanAt = null;
        state._lastProgressAt = Date.now();
        state._lastProgressSignature = getProgressSignature({
          ...payload,
          totalInvoices: newTotalInvoices,
          currentInvoiceIndex: newCurrentIndex,
          processedCount: newProcessedCount
        });
      }
      
      // Handle completion
      if (payload.status === 'completed' || payload.status === 'completed_with_errors') {
        const acceptedCompletion = completeActiveImportSession(state, payload, payload.status, 'websocket_progress_terminal');
        if (!acceptedCompletion) {
          logImportStateWrite(
            'websocket',
            'handleDetailedImportProgress_terminal_rejected',
            previousState,
            getImportDebugSnapshot(state),
            payload
          );
          return;
        }
        state.isImporting = false;
        state.importEndTime = new Date().toISOString();
        state.showImportNotification = false;
        // Don't auto-show modal here - let handleImportComplete handle it
      } else if (['error', 'failed', 'cancelled'].includes(payload.status)) {
        const acceptedCompletion = completeActiveImportSession(state, payload, payload.status, 'websocket_progress_terminal');
        if (!acceptedCompletion) {
          logImportStateWrite(
            'websocket',
            'handleDetailedImportProgress_terminal_rejected',
            previousState,
            getImportDebugSnapshot(state),
            payload
          );
          return;
        }
        state.isImporting = false;
        state.importEndTime = new Date().toISOString();
        state.showImportNotification = false;
      }
      logImportStateWrite(
        'websocket',
        'handleDetailedImportProgress',
        previousState,
        getImportDebugSnapshot(state),
        payload
      );
    },
    // NEW: Handle import completion from WebSocket
    handleImportComplete: (state, action) => {
      const previousState = getImportDebugSnapshot(state);
      const payload = action.payload || {};
      const now = Date.now();
      
      // CRITICAL FIX: Handle both nested stats and top-level stats
      const stats = payload.stats || payload;
      const incomingCompletionSessionId = getPayloadSessionId(payload) || getPayloadSessionId(stats);
      const existingSessionId = state.activeImportSession?.sessionId || state._importSessionId || null;
      const existingActiveSession = Boolean(state.activeImportSession && !TERMINAL_IMPORT_STATUSES.includes(state.activeImportSession.status));
      const incomingCompletionTotal = Number(stats.totalInvoices ?? stats.total_invoices ?? payload.totalInvoices ?? 0);

      if (
        existingActiveSession &&
        existingSessionId &&
        (!incomingCompletionSessionId || incomingCompletionSessionId !== existingSessionId) &&
        incomingCompletionTotal < Number(state.activeImportSession.total || 0)
      ) {
        logImportStateWrite(
          'websocket',
          incomingCompletionSessionId ? 'handleImportComplete_rejected_session_mismatch' : 'handleImportComplete_rejected_batch_completion_without_session_id',
          previousState,
          getImportDebugSnapshot(state),
          {
            ...payload,
            rejectedReason: 'active_import_session_wins',
            incomingCompletionSessionId,
            existingSessionId,
            incomingCompletionTotal,
            activeTotal: state.activeImportSession.total
          }
        );
        return;
      }
      
      const hasCompletedImportWork = (
        Number(stats.successCount || 0) > 0 ||
        Number(stats.createdCount || 0) > 0 ||
        Number(stats.updatedCount || 0) > 0 ||
        Number(stats.unchangedCount || 0) > 0 ||
        Number(stats.processedCount || 0) > 0 ||
        Number(stats.totalInvoices || 0) > 0 ||
        Number(stats.skippedCount || 0) > 0 ||
        Number(stats.alreadySubmittedCount || 0) > 0
      );

      // CRITICAL FIX: Check if this is a true skipped import (duplicate prevention).
      // Successful imports can include skipped/already-submitted invoices in the final summary.
      const isSkipped = !hasCompletedImportWork && (
                        stats.skipped === true ||
                        stats.reason === 'rapid_reimport' ||
                        stats.reason === 'concurrent_import' ||
                        stats.reason === 'duplicate_session' ||
                        payload.message?.includes('skipped') ||
                        payload.message?.includes('too soon') ||
                        payload.message?.includes('duplicate prevention') ||
                        payload.message?.includes('another import'));
      
      // CRITICAL FIX: Check if this is a successful import (not skipped and has actual data)
      const isSuccessful = !isSkipped && (
        (stats.successCount > 0) || 
        (stats.totalInvoices > 0) || 
        (stats.processedCount > 0) ||
        payload.message?.includes('Successfully imported') ||
        (payload.message?.includes('completed') && !payload.message?.includes('skipped'))
      );
      
      // CRITICAL FIX: Generate unique session key for this completion event
      // Include the message type to distinguish between skipped and successful imports
      const messageType = isSkipped ? 'skipped' : (isSuccessful ? 'success' : 'other');
      const completionKey = `${messageType}-${stats.totalInvoices || 0}-${stats.successCount || 0}-${stats.failedCount || 0}`;
      
      // CRITICAL FIX: Check if we've already processed this exact completion
      if (state._lastCompletionKey === completionKey) {
        logImportStateWrite(
          'websocket',
          'handleImportComplete_duplicate_ignored',
          previousState,
          getImportDebugSnapshot(state),
          payload
        );
        return;
      }
      
      // CRITICAL FIX: Deduplication - check if we already processed a completion recently
      // This prevents the modal from showing multiple times for the same import
      const timeSinceLastCompletion = now - (state._lastCompletionTime || 0);
      const minCompletionInterval = 3000; // 3 seconds minimum between completions (reduced from 5s)
      
      // CRITICAL FIX: If we have an active import in progress and receive a skipped message,
      // don't show the skipped message - the active import will complete soon
      const hasActiveImport = state.isImporting || ACTIVE_IMPORT_STATUSES.includes(state.status);
      
      if (isSkipped && hasActiveImport) {
        // Don't update last completion time/key - let the real completion come through
        logImportStateWrite(
          'websocket',
          'handleImportComplete_skipped_ignored_active_import',
          previousState,
          getImportDebugSnapshot(state),
          payload
        );
        return;
      }
      
      // CRITICAL FIX: If this is a successful import, clear any previous skipped message
      if (isSuccessful && state.lastImportResult?.skipped) {
        state.lastImportResult = null;
        state.showLastImportResult = false;
      }
      
      if (timeSinceLastCompletion < minCompletionInterval && state._lastCompletionTime > 0) {
        // Skip this completion - too soon after previous one
        logImportStateWrite(
          'websocket',
          'handleImportComplete_too_soon_ignored',
          previousState,
          getImportDebugSnapshot(state),
          payload
        );
        return;
      }
      
      // CRITICAL FIX: Update last completion timestamp AND key
      state._lastCompletionTime = now;
      state._lastCompletionKey = completionKey;
      
      // CRITICAL FIX: Always update connection status when receiving completion
      state.isConnected = true;
      state.isRunning = true;
      
      // CRITICAL FIX: Handle skipped imports differently
      if (isSkipped) {
        state.status = 'idle'; // Set to idle for skipped imports
        state.isImporting = false;
        state.progress = 0;
        state.importEndTime = new Date().toISOString();
        state.showImportNotification = false;
        
        // Set appropriate message for skipped import
        state.message = stats.message || payload.message || 'Import skipped - duplicate prevention';
        
        setLatestScan(state, 'No import activity detected.', now);
        state.showLastImportResult = hasMeaningfulImportResult(state.lastImportResult);
        
        // Reset import stats since nothing was imported
        state.processedCount = 0;
        state._lastProgressAt = 0;
        state._lastProgressSignature = null;
        state.successCount = 0;
        state.failedCount = 0;
        state.totalInvoices = 0;
        state.currentInvoiceIndex = 0;
        state.currentInvoiceNumber = null;
        
        if (process.env.NODE_ENV === 'development') {
        }
        logImportStateWrite(
          'websocket',
          'handleImportComplete_skipped',
          previousState,
          getImportDebugSnapshot(state),
          payload
        );
        return; // Exit early for skipped imports
      }
      
      const acceptedCompletion = completeActiveImportSession(state, payload, 'completed', 'websocket_completion');
      if (!acceptedCompletion) {
        logImportStateWrite(
          'websocket',
          'handleImportComplete_rejected_by_session_guard',
          previousState,
          getImportDebugSnapshot(state),
          payload
        );
        return;
      }
      state.status = 'completed';
      state.isImporting = false;
      state.progress = 100;
      state.importEndTime = new Date().toISOString();
      state.showImportNotification = false;
      
      if (payload.message) {
        state.message = payload.message;
      }
      
      // CRITICAL FIX: Check if this is a "no new data" completion
      const noNewData = stats.noNewData === true || 
                        stats.dataSync === true ||
                        isNoNewDataImportMessage(stats.message, payload.message);

      const hasMeaningfulPreviousResult = hasMeaningfulImportResult(state.lastImportResult);

      if (noNewData && hasMeaningfulPreviousResult) {
        state.status = 'idle';
        state.isImporting = false;
        state.progress = 0;
        state.showImportNotification = false;
        state.message = stats.message || payload.message || 'No changes detected in database file';
        setLatestScan(state, 'No new changes detected since the last scan.');
        state.showLastImportResult = true;
        logImportStateWrite(
          'websocket',
          'handleImportComplete_no_new_data_previous_result',
          previousState,
          getImportDebugSnapshot(state),
          payload
        );
        return;
      }

      if (noNewData) {
        state.status = 'idle';
        state.isImporting = false;
        state.progress = 0;
        state.showImportNotification = false;
        state.message = stats.message || payload.message || 'No changes detected in database file';
        setLatestScan(state, 'No new changes detected since the last scan.');
        state.showLastImportResult = hasMeaningfulImportResult(state.lastImportResult);
        logImportStateWrite(
          'websocket',
          'handleImportComplete_no_new_data',
          previousState,
          getImportDebugSnapshot(state),
          payload
        );
        return;
      }
      
      const countSummary = getImportCountSummary({ ...payload, ...stats }, {
        terminal: true,
        previousTotal: Number(state.totalInvoices || 0),
        previousProcessed: Number(state.processedCount || 0)
      });
      const finalTotalInvoices = countSummary.total;
      const finalProcessedCount = countSummary.processed;

      if (stats) {
        // Update all stat fields with proper fallbacks
        state.processedCount = finalProcessedCount;
        state.successCount = stats.successCount || 0;
        state.failedCount = stats.failedCount || 0;
        state.totalInvoices = finalTotalInvoices;
        
        // CRITICAL FIX: Also update importResults for the modal display
        state.importResults = {
          successCount: stats.successCount || 0,
          failedCount: stats.failedCount || 0,
          totalInvoices: finalTotalInvoices,
          processedCount: finalProcessedCount,
          validationFailedCount: stats.validationFailedCount || 0,
          alreadySubmittedCount: stats.alreadySubmittedCount || 0,
          failedInvoices: stats.failedInvoices || [],
          failedRecords: stats.failedRecords || [],
          validationFailedInvoices: stats.validationFailedInvoices || [],
          results: stats.results || [],
          noNewData: noNewData,  // Include the flag in importResults
          dataSync: stats.dataSync === true,
          message: stats.message || payload.message || 'Import completed'
        };
      }
      
      // CRITICAL FIX: Save results to lastImportResult for persistent display
      // This ensures results remain visible even after status transitions to idle
      state.lastImportResult = {
        successCount: stats.successCount || 0,
        failedCount: stats.failedCount || 0,
        totalInvoices: finalTotalInvoices,
        processedCount: finalProcessedCount,
        createdCount: stats.createdCount || 0,
        updatedCount: stats.updatedCount || 0,
        unchangedCount: stats.unchangedCount || 0,
        protectedCount: stats.protectedCount || 0,
        conflictCount: stats.conflictCount || 0,
        sourceChangedCount: stats.sourceChangedCount || 0,
        skippedCount: stats.skippedCount || 0,
        alreadySubmittedCount: stats.alreadySubmittedCount || 0,
        skippedReason: stats.skippedReason || null,
        skippedReasonMessage: stats.skippedReasonMessage || null,
        validationFailedCount: stats.validationFailedCount || 0,
        failedInvoices: stats.failedInvoices || [],
        failedRecords: stats.failedRecords || [],
        validationFailedInvoices: stats.validationFailedInvoices || [],
        protectedInvoices: stats.protectedInvoices || [],
        conflictInvoices: stats.conflictInvoices || [],
        sourceChangedInvoices: stats.sourceChangedInvoices || [],
        skippedInvoices: stats.skippedInvoices || [],
        results: stats.results || [],
        noNewData: noNewData,
        dataSync: stats.dataSync === true,
        message: stats.message || payload.message || 'Import completed',
        timestamp: now,
        sessionKey: completionKey
      };
      state.showLastImportResult = true; // Show the last result display
      
      // CRITICAL FIX: Disable modal - show results under Auto-Import Service card instead
      // User prefers non-modal display for both manual and auto-import
      // All import results are displayed under the Auto-Import Service card
      state.showImportResultModal = false; // Never show modal
      state.hasShownImportResultForCurrentImport = true; // Mark as shown to prevent duplicate processing
      
      if (process.env.NODE_ENV === 'development') {
      }
      logImportStateWrite(
        'websocket',
        'handleImportComplete',
        previousState,
        getImportDebugSnapshot(state),
        payload
      );
      
    },
    // NEW: Handle stats update from WebSocket
    handleStatsUpdate: (state, action) => {
      // Stats updates don't change auto-import state directly
      // They are handled by the Dashboard component to update UI
      // But we can track when stats were last updated
      state.lastStatsRefresh = new Date().toISOString();
    },
    // CRITICAL FIX: Reset only the shown flag (for starting new import without full reset)
    resetImportResultShownFlag: (state) => {
      state.hasShownImportResultForCurrentImport = false;
    },
    // CRITICAL FIX: Set manual trigger flag to control progress/modal visibility
    setIsManuallyTriggered: (state, action) => {
      state.isManuallyTriggered = action.payload;
    },
    // NEW: Set trigger source to distinguish user-initiated vs background imports
    setTriggerSource: (state, action) => {
      state.triggerSource = action.payload; // 'manual', 'service-control', 'scheduled', 'auto-connect', 'background'
    },
    // CRITICAL FIX: Reset all import state when service disconnects
    // This prevents stuck progress bars and stale import data
    resetStateOnDisconnection: (state) => {
      const previousState = getImportDebugSnapshot(state);
      if (hasActiveImportSession(state)) {
        state.isConnected = true;
        state.isRunning = true;
        state.message = state.message || 'Import in progress';
        logImportStateWrite(
          'service_status_poll',
          'resetStateOnDisconnection_preserved_active_import',
          previousState,
          getImportDebugSnapshot(state),
          null
        );
        return;
      }
      // Keep connection status as false (disconnected)
      state.isConnected = false;
      state.isRunning = false;
      state.status = 'disconnected';
      state.message = 'Service connection lost';
      state.progress = 0;
      
      // Reset all import-related state
      state.isImporting = false;
      state.importJobId = null;
      state.importType = null;
      state.totalInvoices = 0;
      state.currentInvoiceIndex = 0;
      state.currentInvoiceNumber = null;
      state.processedCount = 0;
      state._lastProgressAt = 0;
      state._lastProgressSignature = null;
      state.successCount = 0;
      state.failedCount = 0;
      state.failedInvoices = [];
      state.importStartTime = null;
      state.importEndTime = null;
      state.importDuration = null;
      state.importResults = null;
      state.showImportResultModal = false;
      state.showImportNotification = false;
      state.isManuallyTriggered = false;
      state.triggerSource = null;
      state.estimatedTimeRemaining = 0;
      state.invoicesPerSecond = 0;
      state._lastApiCallTime = 0; // Reset API call time to allow fresh updates
      
      state.showLastImportResult = hasMeaningfulImportResult(state.lastImportResult);
      logImportStateWrite(
        'service_status_poll',
        'resetStateOnDisconnection',
        previousState,
        getImportDebugSnapshot(state),
        null
      );
    },
    
    // CRITICAL FIX: Reset progress when service stops or disconnects
    resetProgressOnStop: (state) => {
      const previousState = getImportDebugSnapshot(state);
      if (hasActiveImportSession(state)) {
        state.isRunning = true;
        logImportStateWrite(
          'service_status_poll',
          'resetProgressOnStop_preserved_active_import',
          previousState,
          getImportDebugSnapshot(state),
          null
        );
        return;
      }
      state.progress = 0;
      state.status = 'idle';
      state.isImporting = false;
      state.isRunning = false;
      state.message = 'Service stopped';
      state.showImportNotification = false;
      state.currentInvoiceIndex = 0;
      state.processedCount = 0;
      state._lastProgressAt = 0;
      state._lastProgressSignature = null;
      
      state.showLastImportResult = hasMeaningfulImportResult(state.lastImportResult);
      logImportStateWrite(
        'service_status_poll',
        'resetProgressOnStop',
        previousState,
        getImportDebugSnapshot(state),
        null
      );
    },
    clearStaleActiveStatus: (state) => {
      const previousState = getImportDebugSnapshot(state);
      if (ACTIVE_IMPORT_STATUSES.includes(state.status)) {
        state.isRunning = true;
      }
      logImportStateWrite(
        'service_status_poll',
        'clearStaleActiveStatus',
        previousState,
        getImportDebugSnapshot(state),
        null
      );
    },
    // NEW: Dismiss last import result (user manually closes the results)
    dismissLastImportResult: (state) => {
      state.showLastImportResult = false;
      // Keep lastImportResult data for reference, just hide the display
    },
    // NEW: Clear last import result completely (for new import session)
    clearLastImportResult: (state) => {
      const previousState = getImportDebugSnapshot(state);
      state.lastImportResult = null;
      state.showLastImportResult = false;
      logImportStateWrite(
        'dashboard_refresh',
        'clearLastImportResult',
        previousState,
        getImportDebugSnapshot(state),
        null
      );
    },
    clearActiveImportSessionAfterTerminal: (state) => {
      if (!state.activeImportSession || !TERMINAL_IMPORT_STATUSES.includes(state.activeImportSession.status)) {
        return;
      }
      clearActiveImportSession(state, 'terminal_unlock', {
        status: state.activeImportSession.status
      });
    },
    // CRITICAL FIX: Reset all import tracking state (called when reset tracking is triggered)
    resetImportTrackingState: (state) => {
      // Reset all import-related state to initial values
      state.lastImportResult = null;
      state.showLastImportResult = false;
      state.importResults = null;
      clearActiveImportSession(state, 'reset_import_tracking', {
        action: 'resetImportTrackingState'
      });
      state.showImportResultModal = false;
      state.hasShownImportResultForCurrentImport = false;
      state._lastCompletionTime = 0;
      state._lastCompletionKey = null;
      
      // Reset import stats
      state.totalInvoices = 0;
      state.currentInvoiceIndex = 0;
      state.currentInvoiceNumber = null;
      state.processedCount = 0;
      state._lastProgressAt = 0;
      state._lastProgressSignature = null;
      state.successCount = 0;
      state.failedCount = 0;
      state.failedInvoices = [];
      state.progress = 0;
      
      // Reset import session tracking
      state.importJobId = null;
      state.importType = null;
      state.importStartTime = null;
      state.importEndTime = null;
      state.importDuration = null;
      state.isImporting = false;
      state.isManuallyTriggered = false;
      state.triggerSource = null;
      
      // Reset status to idle
      state.status = 'idle';
      state.message = 'Import tracking reset - ready for new import';

    },
    forceCompleteImport(state) {
      // Safety valve: called when progress is stuck at 99% for too long without import_complete
      if (state.activeImportSession) {
        completeActiveImportSession(state, {
          totalInvoices: state.activeImportSession.total,
          processedCount: state.activeImportSession.processed,
          message: 'Import completed'
        }, 'completed', 'force_complete_import');
      }
      state.progress = 100;
      state.status = 'completed';
      state.isImporting = false;
      state.message = 'Import completed';
    }
  }
});

export const {
  clearError,
  setShowImportNotification,
  setLastStatsRefresh,
  resetAutoImportState,
  setIsConnected,
  setProgress,
  setStatus,
  setMessage,
  setIsLoading,
  // NEW: Export manual import actions
  setImportJobId,
  setIsImporting,
  setImportType,
  setShowImportResultModal,
  updateImportProgress,
  setImportResults,
  resetImportState,
  // CRITICAL FIX: Export manual trigger flag setter
  setIsManuallyTriggered,
  // NEW: Export trigger source setter
  setTriggerSource,
  // CRITICAL FIX: Export disconnection state reset
  resetStateOnDisconnection,
  // CRITICAL FIX: Export progress reset on stop
  resetProgressOnStop,
  clearStaleActiveStatus,
  // CRITICAL FIX: Export import result shown flag reset
  resetImportResultShownFlag,
  // NEW: Export last import result dismiss and clear actions
  dismissLastImportResult,
  clearLastImportResult,
  clearActiveImportSessionAfterTerminal,
  // CRITICAL FIX: Export reset import tracking state
  resetImportTrackingState,
  forceCompleteImport
} = autoImportSlice.actions;

// NOTE: handleAutoImportStatusUpdate, handleDetailedImportProgress (standalone),
// and handleImportCompleteUpdate are registered as slice reducers below.
// The standalone exports at lines 74, 195, 321 are aliases for the reducers
// in the reducers: block and should not be used directly — use dispatch().

// Canonical connection state for the auto-import service. The connection badge MUST use this
// (not raw isConnected) so a stale status doc or stale local import context can never show
// "Connected". Uses the backend canonical fields: isConnected, isHealthy, isRunning,
// serviceStatus, and secondsSinceLastHeartbeat (heartbeat staleness independently forces
// Disconnected per the configured threshold).
export const selectAutoImportConnection = (state) => {
  const s = state.autoImport || {};
  const secondsSinceHeartbeat = Number.isFinite(Number(s.secondsSinceLastHeartbeat))
    ? Number(s.secondsSinceLastHeartbeat)
    : null;
  const serviceStatus = s.serviceStatus?.status || s.status || 'unknown';
  const heartbeatStale = secondsSinceHeartbeat != null && secondsSinceHeartbeat > LIVE_HEARTBEAT_THRESHOLD_SECONDS;
  const statusSaysDown = DISCONNECTED_SERVICE_STATUSES.includes(serviceStatus);
  const isConnected = s.isConnected === true;
  const isHealthy = s.isHealthy === true;
  const isRunning = s.isRunning === true;
  const isLive = isConnected && isHealthy && !heartbeatStale && !statusSaysDown;
  return {
    isConnected,
    isHealthy,
    isRunning,
    serviceStatus,
    secondsSinceLastHeartbeat: secondsSinceHeartbeat,
    lastHeartbeat: s.lastHeartbeat || s.serviceStatus?.lastActivity || null,
    heartbeatStale,
    isLive,
    displayStatus: isLive ? 'connected' : (s.isLoading ? 'connecting' : 'disconnected')
  };
};

export default autoImportSlice.reducer;
